import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import { useGlobalCategories, useCreateGlobalCategory } from '../../hooks/useGlobalCategories';
import { useObjectCategories } from '../../hooks/useObjectCategories';
import { useParentCategories } from '../../hooks/useParentCategories';
import { useLabelAttributes } from '../../hooks/useLabelAttributes';
import AttributePanel from '../../components/AttributePanel';
import { LabelAttribute } from '../../types/labelAttribute';
import { 
  getRequiredSteps, 
  getLabelsForStep, 
  stepKeyFromMedia,
  stepKeyToRole,
  getStepDisplayLabel,
  type LabelData
} from '../../utils/submissionHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Multi-photo types and helpers
// ─────────────────────────────────────────────────────────────────────────────
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface SubmissionMediaItem {
  role: MediaRole;
  url: string;
  type?: string;
  storagePath?: string;
}

// Check if submission is multi-photo (has media[] with 2+ items)
function isMultiPhotoSubmission(submission: any): boolean {
  return Array.isArray(submission?.media) && submission.media.length >= 2;
}

// Get required roles for a submission (legacy function, now uses stepKeys internally)
// Phase A: Uses stepKeys internally but returns roles for UI compatibility
function getRequiredRoles(submission: any): MediaRole[] {
  // Use stepKeys internally (derives from submission.media, NOT task.steps)
  const stepKeys = getRequiredSteps(submission);
  
  // Map stepKeys back to roles for UI display (legacy compatibility)
  const roles: MediaRole[] = [];
  for (const stepKey of stepKeys) {
    const role = stepKeyToRole(stepKey, submission);
    if (role && !roles.includes(role)) {
      roles.push(role);
    }
  }
  
  // Fallback: if no roles found, use stepKey as role (for legacy compatibility)
  if (roles.length === 0) {
    return ['primary'];
  }
  
  return roles;
}

// Get image URL for a specific role
function getImageUrlForRole(submission: any, role: MediaRole): string | null {
  if (role === 'primary') {
    // Prefer mediaUrl for backward compatibility
    if (submission?.mediaUrl) return submission.mediaUrl;
    // Fallback to media array
    if (Array.isArray(submission?.media)) {
      const primaryMedia = submission.media.find((m: any) => m.role === 'primary');
      if (primaryMedia?.url) return primaryMedia.url;
    }
    // Legacy fallback
    if (submission?.imageUrl) return submission.imageUrl;
    return null;
  }
  
  // For secondary roles, look in media array
  if (Array.isArray(submission?.media)) {
    const mediaItem = submission.media.find((m: any) => m.role === role);
    return mediaItem?.url || null;
  }
  
  return null;
}

// Get display label for a role
function getRoleDisplayLabel(role: MediaRole, submission?: any): string {
  if (role === 'primary') {
    // Check if this is a before_after task
    if (submission?.media?.some((m: any) => m.role === 'after')) {
      return 'Before';
    }
    return 'Primary';
  }
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

interface Submission {
  id: string;
  taskId: string;
  imageUrl?: string;
  mediaUrl?: string;
  category: string;
  taskType: string;
  taskTitle: string;
  createdAt: any;
  status: string;
  userId: string;
  qaReviewStatus?: string;
  qaReviewReason?: string;
  // Single-photo labels (legacy/default)
  labels?: LabelData[];
  // Multi-photo support
  media?: SubmissionMediaItem[];
  labelsByRole?: Partial<Record<MediaRole, LabelData[]>>;
  // New stepKey-based labels (Phase D)
  labelsByStep?: Partial<Record<string, LabelData[]>>;
  imageAttributes?: Record<string, any>;
}

interface TaskStep {
  stepId: string;
  title: string;
  description: string;
  required: boolean;
  exampleImages: string[];
  dropdown?: {
    enabled: boolean;
    id: string;
    prompt: string;
    required: boolean;
    options: { label: string; value: string }[];
  };
}

interface Task {
  id: string;
  title: string;
  steps?: TaskStep[];
  detection?: {
    enabled: boolean;
    schemaRef?: {
      id: string;
      version: string;
    };
  };
  validationRules?: {
    expectedLabels?: string[];
    expectedLabelCategories?: string[];
  };
}

interface BoundingBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  categoryId: string;
  attributes: Record<string, any>;
  sourceLabel?: any;
}

function serializeBoxToLabel(b: BoundingBox, scale: number) {
  const x1 = Math.round(b.x / scale);
  const y1 = Math.round(b.y / scale);
  const x2 = Math.round((b.x + b.width) / scale);
  const y2 = Math.round((b.y + b.height) / scale);
  return {
    ...(b.sourceLabel || {}),
    categoryId: b.categoryId,
    bbox: [x1, y1, x2, y2],
    attributes: b.attributes || {},
  };
}

function resolveAttributeDefinitions(
  names: string[] | undefined,
  catalog: LabelAttribute[]
): LabelAttribute[] {
  if (!names?.length) return [];
  return names
    .map((name) => catalog.find((attr) => attr.name === name && attr.active !== false))
    .filter((attr): attr is LabelAttribute => Boolean(attr));
}

function hasExistingAttributeValue(value: any): boolean {
  return value !== undefined && value !== null && value !== '';
}

const CLASSID_PARSEABLE_CATEGORIES = new Set([
  'food',
  'dishes',
  'cleaning',
  'laundry',
  'fridge',
  'trash',
]);

const DECAY_STATE_SEVERITY: Record<string, number> = {
  no_visible_decay: 1,
  visible_rot: 2,
  visible_mold: 3,
};

function deriveAttributesFromClassId(
  classId: string | undefined | null,
  category: string | undefined | null
): Record<string, any> {
  if (!classId || typeof classId !== 'string' || !classId.trim()) return {};
  if (!category || typeof category !== 'string' || !category.trim()) return {};
  if (!CLASSID_PARSEABLE_CATEGORIES.has(category.trim().toLowerCase())) return {};

  const tokens = classId.toLowerCase().split('_').filter(Boolean);
  const result: Record<string, any> = {};
  const defectFlags: string[] = [];
  let bestDecayValue: string | null = null;
  let bestDecaySeverity = 0;

  const applyDecay = (value: string) => {
    const severity = DECAY_STATE_SEVERITY[value] ?? 0;
    if (severity > bestDecaySeverity) {
      bestDecaySeverity = severity;
      bestDecayValue = value;
    }
  };

  const addDefectFlag = (flag: string) => {
    if (!defectFlags.includes(flag)) defectFlags.push(flag);
  };

  for (const token of tokens) {
    switch (token) {
      case 'fresh':
        result.ripeness = 'ripe';
        applyDecay('no_visible_decay');
        break;
      case 'ripe':
        result.ripeness = 'ripe';
        break;
      case 'unripe':
      case 'underripe':
        result.ripeness = 'underripe';
        break;
      case 'overripe':
        result.ripeness = 'overripe';
        break;
      case 'moldy':
      case 'mold':
        applyDecay('visible_mold');
        break;
      case 'spoiled':
      case 'rotten':
      case 'rotting':
        applyDecay('visible_rot');
        break;
      case 'bruised':
        addDefectFlag('bruised');
        break;
      case 'wilted':
      case 'wilting':
        addDefectFlag('wilted');
        break;
      case 'sprouting':
        addDefectFlag('sprouting');
        break;
      case 'dried':
        addDefectFlag('dried_out');
        break;
      case 'wrinkled':
        addDefectFlag('wrinkled');
        result.configuration = 'wrinkled';
        break;
      case 'slimy':
        addDefectFlag('slimy');
        break;
      case 'discoloured':
      case 'discolored':
      case 'yellowing':
      case 'darkening':
        addDefectFlag('discoloured');
        break;
      case 'clean':
        result.soil_level = 'clean';
        break;
      case 'dirty':
        result.soil_level = 'light_soil';
        break;
      case 'empty':
        result.fill_level = 'empty';
        break;
      case 'full':
        result.fill_level = 'high';
        break;
      case 'overflowing':
        result.fill_level = 'overflowing';
        break;
      case 'folded':
        result.configuration = 'folded';
        break;
      case 'hung':
      case 'hanging':
        result.configuration = 'hanging';
        break;
      case 'unfolded':
        result.configuration = 'wrinkled';
        break;
      case 'stained':
        result.soil_level = 'light_soil';
        break;
      case 'hole':
        result.damage = 'hole';
        break;
      case 'partially':
        result.fill_level = 'medium';
        break;
      default:
        break;
    }
  }

  if (defectFlags.length > 0) {
    result.defect_flags = defectFlags;
  }
  if (bestDecayValue) {
    result.decay_state = bestDecayValue;
  }

  return result;
}

function computeDerivedFromClassId(
  task: Task | null | undefined,
  submission: Submission
): Record<string, any> {
  const taskAny = task as any;
  const classId = taskAny?.classification?.classId;
  const category = taskAny?.category ?? submission?.category;
  return deriveAttributesFromClassId(classId, category);
}

function getMetadataValue(
  attrName: string,
  task: Task | null,
  submission: Submission | null,
  derivedFromClassId?: Record<string, any>
): any {
  if (
    derivedFromClassId &&
    Object.prototype.hasOwnProperty.call(derivedFromClassId, attrName)
  ) {
    const derived = derivedFromClassId[attrName];
    if (derived !== undefined) return derived;
  }
  if (task && Object.prototype.hasOwnProperty.call(task, attrName)) {
    const value = (task as any)[attrName];
    if (value !== undefined) return value;
  }
  if (submission && Object.prototype.hasOwnProperty.call(submission, attrName)) {
    const value = (submission as any)[attrName];
    if (value !== undefined) return value;
  }
  return undefined;
}

function prefillImageAttributeValues(
  loaded: Record<string, any>,
  attributeDefs: LabelAttribute[],
  task: Task | null,
  submission: Submission | null,
  derivedFromClassId?: Record<string, any>
): Record<string, any> {
  const values = { ...loaded };
  for (const attr of attributeDefs) {
    if (hasExistingAttributeValue(values[attr.name])) continue;
    const metadataValue = getMetadataValue(
      attr.name,
      task,
      submission,
      derivedFromClassId
    );
    if (metadataValue !== undefined) {
      values[attr.name] = metadataValue;
    } else if (
      !attr.required &&
      attr.defaultValue !== undefined &&
      attr.defaultValue !== null
    ) {
      values[attr.name] = attr.defaultValue;
    }
  }
  return values;
}

function getClassAttributeNames(
  categoryId: string,
  schema: any,
  getCategoryById: (id: string) => any
): string[] {
  const schemaClass = schema?.classes?.find(
    (cls: any) => cls.id === categoryId || cls.name === categoryId
  );
  if (schemaClass?.attributes?.length) return schemaClass.attributes;

  const globalCategory = getCategoryById(categoryId);
  if (
    globalCategory &&
    Array.isArray((globalCategory as any).attributes) &&
    (globalCategory as any).attributes.length
  ) {
    return (globalCategory as any).attributes;
  }
  return [];
}

function getInstanceAttributeDefsForBox(
  box: BoundingBox,
  schema: any,
  catalog: LabelAttribute[],
  getCategoryById: (id: string) => any
): LabelAttribute[] {
  const globalNames: string[] = schema?.globalAttributes || [];
  const classNames = getClassAttributeNames(box.categoryId, schema, getCategoryById);
  const allNames = Array.from(new Set([...globalNames, ...classNames]));
  return resolveAttributeDefinitions(allNames, catalog);
}

function prefillBoxAttributeValues(
  box: BoundingBox,
  attributeDefs: LabelAttribute[],
  task: Task | null,
  submission: Submission | null,
  derivedFromClassId?: Record<string, any>
): BoundingBox {
  const attrs = { ...(box.attributes || {}) };
  let usedTaskMetadata = false;

  for (const attr of attributeDefs) {
    if (hasExistingAttributeValue(attrs[attr.name])) continue;
    const metadataValue = getMetadataValue(
      attr.name,
      task,
      submission,
      derivedFromClassId
    );
    if (metadataValue !== undefined) {
      attrs[attr.name] = metadataValue;
      usedTaskMetadata = true;
    } else if (
      !attr.required &&
      attr.defaultValue !== undefined &&
      attr.defaultValue !== null
    ) {
      attrs[attr.name] = attr.defaultValue;
    }
  }

  if (usedTaskMetadata) {
    attrs.state_label_source = 'task_metadata';
  }

  return { ...box, attributes: attrs };
}

export function isAttributeAnswered(attr: LabelAttribute, value: any): boolean {
  if (attr.type === 'enum' || attr.type === 'text') {
    return typeof value === 'string' && value.length > 0;
  }
  if (attr.type === 'multi_enum') {
    return Array.isArray(value) && value.length >= 1;
  }
  if (attr.type === 'boolean') {
    return value === true || value === false;
  }
  if (attr.type === 'number') {
    return typeof value === 'number' && !Number.isNaN(value);
  }
  return false;
}

export default function WorkbenchEfficient() {
  const navigate = useNavigate();
  
  // Data state
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  // Queue filters
  const [filters, setFilters] = useState({
    category: 'all',
    subcategory: 'all',
    itemType: 'all',
    task: 'all',
  });
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [schemas, setSchemas] = useState<Record<string, any>>({});
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(null);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [currentSchema, setCurrentSchema] = useState<any>(null);
  const [history, setHistory] = useState<Submission[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Canvas state
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [boxes, setBoxes] = useState<BoundingBox[]>([]);
  const boxesRef = useRef<BoundingBox[]>([]); // Ref to track boxes for immediate drawing
  const [selectedBox, setSelectedBox] = useState<BoundingBox | null>(null);
  const [flaggedLabels, setFlaggedLabels] = useState<Array<{
    categoryId: string;
    bbox: [number, number, number, number];
    flagReason?: string;
    flagType?: string;
  }>>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  // Temporary preview box while dragging
  const [draftBox, setDraftBox] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  
  // Hide/show labels state
  const [hideAllLabels, setHideAllLabels] = useState(false);
  const [hiddenBoxIds, setHiddenBoxIds] = useState<Set<string>>(new Set());
  const [undoHistory, setUndoHistory] = useState<BoundingBox[][]>([]);
  const [imageAttributeValues, setImageAttributeValues] = useState<Record<string, any>>({});
  const [derivedFromClassId, setDerivedFromClassId] = useState<Record<string, any>>({});
  
  // ─────────────────────────────────────────────────────────────────────────────
  // Multi-photo per-role state
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeRole, setActiveRole] = useState<MediaRole>('primary');
  const [activeStepKey, setActiveStepKey] = useState<string>('primary'); // For step-based tasks
  const [boxesByRole, setBoxesByRole] = useState<Partial<Record<MediaRole, BoundingBox[]>>>({});
  const [flaggedLabelsByRole, setFlaggedLabelsByRole] = useState<Partial<Record<MediaRole, LabelData[]>>>({});
  const [undoHistoryByRole, setUndoHistoryByRole] = useState<Partial<Record<MediaRole, BoundingBox[][]>>>({});
  const [savedRoles, setSavedRoles] = useState<Set<MediaRole>>(new Set()); // Track which roles have been saved
  
  // QA Review modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [qaReason, setQaReason] = useState("");
  
  // QA Review reasons for Workbench
  const qaReviewReasons = [
    "Labels Don't Exist",
    "Incorrect Task",
    "Task doesn't look correct",
    "Not Sure if Task is Correct",
    "Image Quality Questionable",
    "Questionable Approval",
    "inappropriate",
    "Possible Duplicate",
    "Looks like AI",
    "Looks like Computer Screen"
  ];
  
  // Global objects and palette controls
  const { categories: globalCategories, getCategoryById } = useGlobalCategories();
  const { categories: objectCategories } = useObjectCategories();
  const { categories: parentCategories } = useParentCategories();
  const { createCategory } = useCreateGlobalCategory();
  const { attributes: labelAttributes } = useLabelAttributes();
  const [showDetectionCategories, setShowDetectionCategories] = useState(false);
  const [showExpectedObjects, setShowExpectedObjects] = useState(true);
  const [showGlobalLibrary, setShowGlobalLibrary] = useState(true);
  const [expandedGlobalGroups, setExpandedGlobalGroups] = useState<Record<string, boolean>>({});
  const [expandedParentGroups, setExpandedParentGroups] = useState<Record<string, boolean>>({});
  const [globalGroupSearchTerms, setGlobalGroupSearchTerms] = useState<Record<string, string>>({});
  const [globalLibrarySearchTerm, setGlobalLibrarySearchTerm] = useState('');
  const [showCreateObjectModal, setShowCreateObjectModal] = useState(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to get category name from either schema or global categories
  const getCategoryName = (categoryId: string) => {
    // First try to find in schema classes
    const schemaCategory = currentSchema?.classes?.find((c: any) => c.id === categoryId);
    if (schemaCategory) {
      return schemaCategory.label || schemaCategory.name || categoryId;
    }
    
    // Then try to find in global categories
    const globalCategory = getCategoryById(categoryId);
    if (globalCategory) {
      return globalCategory.displayName || globalCategory.name || categoryId;
    }
    
    // Fallback to categoryId
    return categoryId;
  };

  const expectedLabelValues = currentTask?.validationRules?.expectedLabels || [];

  const resolvedExpectedLabels = useMemo(() => {
    return expectedLabelValues
      .map(label => label?.trim())
      .filter((label): label is string => Boolean(label))
      .map(label => {
        const normalized = label.toLowerCase();
        const schemaMatch = currentSchema?.classes?.find((cls: any) => {
          const idMatch = cls.id?.toLowerCase() === normalized;
          const nameMatch = cls.name?.toLowerCase() === normalized;
          const displayMatch = cls.displayName?.toLowerCase() === normalized || cls.label?.toLowerCase() === normalized;
          return idMatch || nameMatch || displayMatch;
        });
        if (schemaMatch) {
          return {
            key: `schema-${schemaMatch.id}`,
            display: schemaMatch.displayName || schemaMatch.label || schemaMatch.name || schemaMatch.id,
            targetId: schemaMatch.id,
            original: label,
            source: 'schema' as const,
            missing: false,
          };
        }

        const globalMatch = globalCategories.find(cat => {
          const id = cat.id?.toLowerCase();
          const name = cat.name?.toLowerCase();
          const displayName = cat.displayName?.toLowerCase();
          return id === normalized || name === normalized || displayName === normalized;
        });
        if (globalMatch) {
          return {
            key: `global-${globalMatch.id}`,
            display: globalMatch.displayName || globalMatch.name || globalMatch.id,
            targetId: globalMatch.id,
            original: label,
            source: 'global' as const,
            missing: false,
          };
        }

        return {
          key: `missing-${label}`,
          display: label,
          targetId: label,
          original: label,
          source: 'unknown' as const,
          missing: true,
        };
      });
  }, [expectedLabelValues, currentSchema, globalCategories]);

  const objectCategoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    objectCategories.forEach(cat => {
      map.set(cat.id, cat.displayName || cat.name || cat.id);
    });
    return map;
  }, [objectCategories]);

  const objectCategoryById = useMemo(() => {
    const map = new Map<string, (typeof objectCategories)[number]>();
    objectCategories.forEach(cat => map.set(cat.id, cat));
    return map;
  }, [objectCategories]);

  const parentCategoryById = useMemo(() => {
    const map = new Map<string, (typeof parentCategories)[number]>();
    parentCategories.forEach(cat => map.set(cat.id, cat));
    return map;
  }, [parentCategories]);

  const imageLevelAttributes = useMemo(
    () => resolveAttributeDefinitions(currentSchema?.imageAttributes, labelAttributes),
    [currentSchema?.imageAttributes, labelAttributes]
  );

  const selectedBoxInstanceAttributes = useMemo(() => {
    if (!selectedBox) return [];
    return getInstanceAttributeDefsForBox(
      selectedBox,
      currentSchema,
      labelAttributes,
      getCategoryById
    );
  }, [selectedBox, currentSchema, labelAttributes, getCategoryById]);

  const handleBoxAttributeChange = (next: Record<string, any>) => {
    if (!selectedBox) return;

    const prevAttributes = selectedBox.attributes || {};
    const nextAttributes = { ...next, state_label_source: 'annotator_verified' };

    const applyAttributeChange = (box: BoundingBox): BoundingBox => {
      if (box.id !== selectedBox.id) return box;

      let updatedSourceLabel = box.sourceLabel ? { ...box.sourceLabel } : {};
      const existingFlags = updatedSourceLabel.attributeFlags as Record<string, unknown> | undefined;

      if (existingFlags && Object.keys(existingFlags).length > 0) {
        const nextFlags = { ...existingFlags };
        for (const attrName of Object.keys(existingFlags)) {
          if (prevAttributes[attrName] !== next[attrName]) {
            delete nextFlags[attrName];
          }
        }
        if (Object.keys(nextFlags).length === 0) {
          const { attributeFlags: _removed, ...rest } = updatedSourceLabel;
          updatedSourceLabel = rest;
        } else {
          updatedSourceLabel = { ...updatedSourceLabel, attributeFlags: nextFlags };
        }
      }

      return {
        ...box,
        attributes: nextAttributes,
        sourceLabel: updatedSourceLabel,
      };
    };

    setBoxes((prev) => {
      const updated = prev.map(applyAttributeChange);
      boxesRef.current = updated;
      return updated;
    });
    setSelectedBox((prev) => (prev ? applyAttributeChange(prev) : null));
  };

  const buildPrefillContext = (
    submission: Submission,
    task: Task | null | undefined,
    schema: any,
    derivedFromClassIdMap: Record<string, any>
  ) => ({
    schema,
    task: task ?? null,
    submission,
    labelAttributes,
    getCategoryById,
    derivedFromClassId: derivedFromClassIdMap,
  });

  const prefillNewBox = (box: BoundingBox): BoundingBox => {
    if (!currentSchema) return box;
    const attributeDefs = getInstanceAttributeDefsForBox(
      box,
      currentSchema,
      labelAttributes,
      getCategoryById
    );
    return prefillBoxAttributeValues(
      box,
      attributeDefs,
      currentTask,
      currentSubmission,
      derivedFromClassId
    );
  };

  const validateRequiredAttributes = (): { ok: boolean; messages: string[] } => {
    const messages: string[] = [];
    if (!currentSubmission || !currentSchema) {
      return { ok: true, messages: [] };
    }

    for (const attr of imageLevelAttributes) {
      if (attr.required && !isAttributeAnswered(attr, imageAttributeValues[attr.name])) {
        messages.push(`Image: ${attr.displayName} is required.`);
      }
    }

    const validateBoxesForStep = (stepBoxes: BoundingBox[], stepLabel: string) => {
      stepBoxes.forEach((box, boxIndex) => {
        const defs = getInstanceAttributeDefsForBox(
          box,
          currentSchema,
          labelAttributes,
          getCategoryById
        );
        const className = getCategoryName(box.categoryId);
        for (const attr of defs) {
          if (attr.required && !isAttributeAnswered(attr, box.attributes?.[attr.name])) {
            messages.push(
              `${stepLabel}, box ${boxIndex + 1} (${className}): ${attr.displayName} is required.`
            );
          }
        }
      });
    };

    const stepKeys = getRequiredSteps(currentSubmission);
    const isMulti = isMultiPhotoSubmission(currentSubmission);

    if (!isMulti) {
      validateBoxesForStep(boxes, 'Step 1');
    } else {
      stepKeys.forEach((stepKey, stepIndex) => {
        const role = stepKeyToRole(stepKey, currentSubmission) || 'primary';
        const stepLabel = `Step ${stepIndex + 1}`;
        const stepBoxes = role === activeRole ? boxes : boxesByRole[role] || [];
        validateBoxesForStep(stepBoxes, stepLabel);
      });
    }

    return { ok: messages.length === 0, messages };
  };

  const showAttributeValidationAlert = (messages: string[]) => {
    const shown = messages.slice(0, 8);
    const rest = messages.length - shown.length;
    let alertText = shown.join('\n');
    if (rest > 0) {
      alertText += `\n...and ${rest} more.`;
    }
    alert(alertText);
  };

  const requiredUnansweredCount = useMemo(
    () => validateRequiredAttributes().messages.length,
    [
      boxes,
      boxesByRole,
      activeRole,
      imageAttributeValues,
      currentSchema,
      currentSubmission,
      imageLevelAttributes,
      labelAttributes,
      getCategoryById,
    ]
  );

  const globalCategoryGroups = useMemo(() => {
    const groups: Record<string, typeof globalCategories> = {};
    
    // Get the selected global category IDs from the schema
    const selectedCategoryIds = currentSchema?.selectedGlobalCategories || [];
    
    // If no categories are selected in the schema, return empty groups
    if (selectedCategoryIds.length === 0) {
      return groups;
    }

    // Detection Objects wins: hide Object Library entries whose name matches a schema class
    // or whose id is pinned in featuredObjects (one list or the other, never both).
    const schemaClassNames = new Set(
      (currentSchema?.classes || [])
        .map((c: any) => (typeof c?.name === 'string' ? c.name.trim().toLowerCase() : ''))
        .filter(Boolean)
    );
    const featuredObjectIds = new Set(
      (currentSchema?.featuredObjects || [])
        .map((f: any) => f?.objectId)
        .filter(Boolean)
    );
    
    // Filter global categories to only include those whose subcategory field matches
    // one of the selected category IDs in the schema
    // NOTE: subcategory is the renamed field (was category)
    globalCategories.forEach(cat => {
      const groupKey = cat.subcategory || cat.category || 'uncategorized'; // Fallback to old category for migration
      
      // Only include this category if its groupKey is in the selected list
      if (selectedCategoryIds.includes(groupKey)) {
        const catName = typeof cat.name === 'string' ? cat.name.trim().toLowerCase() : '';
        if (catName && schemaClassNames.has(catName)) {
          return;
        }
        if (featuredObjectIds.has(cat.id)) {
          return;
        }
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(cat);
      }
    });
    return groups;
  }, [globalCategories, currentSchema]);

  const globalCategoryGroupKeys = useMemo(
    () => Object.keys(globalCategoryGroups).sort((a, b) => a.localeCompare(b)),
    [globalCategoryGroups]
  );

  const formatGroupLabel = (value: string) => {
    if (!value) return 'Uncategorized';
    const known = objectCategoryNameMap.get(value);
    if (known) return known;
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Track which categories have been used (have boxes)
  const usedCategoryIds = useMemo(() => {
    const used = new Set<string>();
    boxes.forEach(box => {
      if (box.categoryId) {
        used.add(box.categoryId);
      }
    });
    return used;
  }, [boxes]);

  const handleGlobalGroupToggle = (groupKey: string) => {
    setExpandedGlobalGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const handleParentToggle = (parentKey: string) => {
    setExpandedParentGroups(prev => ({
      ...prev,
      [parentKey]: !prev[parentKey]
    }));
  };

  // Check if we should use stepKey-based tabs (task has steps OR multiple unique stepKeys)
  const shouldUseStepKeyTabs = useMemo(() => {
    if (!currentSubmission || !currentTask) return false;
    const hasTaskSteps = currentTask.steps && Array.isArray(currentTask.steps) && currentTask.steps.length > 0;
    if (hasTaskSteps) return true;
    
    // Check if submission has multiple unique stepKeys
    const stepKeys = getRequiredSteps(currentSubmission);
    return stepKeys.length > 1;
  }, [currentSubmission, currentTask]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Multi-photo step/role switching
  // ─────────────────────────────────────────────────────────────────────────────
  const switchStepKey = (newStepKey: string) => {
    if (!currentSubmission || newStepKey === activeStepKey) return;
    
    // Find media item by stepKey (NOT role)
    const mediaItem = currentSubmission.media?.find((m: any) => stepKeyFromMedia(m) === newStepKey);
    if (!mediaItem) return;
    
    // Persist current step's state (using activeRole for state lookup)
    setBoxesByRole(prev => ({
      ...prev,
      [activeRole]: [...boxesRef.current]
    }));
    setUndoHistoryByRole(prev => ({
      ...prev,
      [activeRole]: [...undoHistory]
    }));
    
    // Load new step's state (find role for state lookup)
    const newRole = mediaItem.role || 'primary';
    const newBoxes = boxesByRole[newRole] || [];
    const newUndoHistory = undoHistoryByRole[newRole] || [];
    
    boxesRef.current = newBoxes;
    setBoxes(newBoxes);
    setUndoHistory(newUndoHistory);
    const stepLabels = getLabelsForStep(currentSubmission, newStepKey);
    setFlaggedLabels(stepLabels.filter((l: any) => l.flagged) || []);
    
    // Reset selection state
    setSelectedBox(null);
    setHideAllLabels(false);
    setHiddenBoxIds(new Set());
    
    // Update active stepKey and role
    setActiveStepKey(newStepKey);
    setActiveRole(newRole);
    
    // Load the image for the new step (use mediaItem.url directly)
    const imageUrl = mediaItem.url;
    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        requestAnimationFrame(() => {
          resizeCanvasForImage(img);
          setTimeout(() => {
            drawCanvas(newBoxes);
          }, 100);
        });
      };
      img.onerror = () => {
        // Try without CORS
        const imgRetry = new Image();
        imgRetry.onload = () => {
          setImage(imgRetry);
          requestAnimationFrame(() => {
            resizeCanvasForImage(imgRetry);
            setTimeout(() => {
              drawCanvas(newBoxes);
            }, 100);
          });
        };
        imgRetry.src = imageUrl;
      };
      img.src = imageUrl;
    }
  };

  const switchRole = (newRole: MediaRole) => {
    if (!currentSubmission || newRole === activeRole) return;
    
    // Persist current role's state
    setBoxesByRole(prev => ({
      ...prev,
      [activeRole]: [...boxesRef.current]
    }));
    setUndoHistoryByRole(prev => ({
      ...prev,
      [activeRole]: [...undoHistory]
    }));
    
    // Load new role's state
    const newBoxes = boxesByRole[newRole] || [];
    const newUndoHistory = undoHistoryByRole[newRole] || [];
    
    boxesRef.current = newBoxes;
    setBoxes(newBoxes);
    setUndoHistory(newUndoHistory);
    setFlaggedLabels(flaggedLabelsByRole[newRole] || []);
    
    // Reset selection state
    setSelectedBox(null);
    setHideAllLabels(false);
    setHiddenBoxIds(new Set());
    
    // Update active role and stepKey
    setActiveRole(newRole);
    const mediaItem = currentSubmission.media?.find((m: any) => m.role === newRole);
    setActiveStepKey(stepKeyFromMedia(mediaItem));
    
    // Load the image for the new role
    const imageUrl = getImageUrlForRole(currentSubmission, newRole);
    if (imageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        requestAnimationFrame(() => {
          resizeCanvasForImage(img);
          setTimeout(() => {
            drawCanvas(newBoxes);
          }, 100);
        });
      };
      img.onerror = () => {
        // Try without CORS
        const imgRetry = new Image();
        imgRetry.onload = () => {
          setImage(imgRetry);
          requestAnimationFrame(() => {
            resizeCanvasForImage(imgRetry);
            setTimeout(() => {
              drawCanvas(newBoxes);
            }, 100);
          });
        };
        imgRetry.src = imageUrl;
      };
      img.src = imageUrl;
    }
  };

  const handleGlobalGroupSearchChange = (groupKey: string, value: string) => {
    setGlobalGroupSearchTerms(prev => ({
      ...prev,
      [groupKey]: value
    }));
  };

  const getObjectDisplayName = (obj: { displayName?: string; label?: string; name?: string; id?: string }) => {
    return obj?.displayName || obj?.label || obj?.name || obj?.id || '';
  };

  const OTHER_PARENT_KEY = '__other__';

  type GlobalLibrarySubcategoryGroup = {
    groupKey: string;
    displayName: string;
    objects: typeof globalCategories;
  };

  type GlobalLibraryParentGroup = {
    parentKey: string;
    displayName: string;
    subcategories: GlobalLibrarySubcategoryGroup[];
    objectCount: number;
  };

  const globalLibraryParentGroups = useMemo((): GlobalLibraryParentGroup[] => {
    const parentMap = new Map<string, { displayName: string; subs: Map<string, typeof globalCategories> }>();

    const ensureParent = (parentKey: string, displayName: string) => {
      if (!parentMap.has(parentKey)) {
        parentMap.set(parentKey, { displayName, subs: new Map() });
      }
      return parentMap.get(parentKey)!;
    };

    Object.entries(globalCategoryGroups).forEach(([groupKey, objects]) => {
      const subcategory = objectCategoryById.get(groupKey);
      const parentId = subcategory?.parentCategoryId;
      const parent = parentId ? parentCategoryById.get(parentId) : undefined;

      const parentKey = parent ? parent.id : OTHER_PARENT_KEY;
      const parentDisplayName = parent
        ? (parent.displayName || parent.name || parent.id)
        : 'Other';

      const entry = ensureParent(parentKey, parentDisplayName);
      const sortedObjects = [...objects].sort((a, b) =>
        getObjectDisplayName(a).localeCompare(getObjectDisplayName(b))
      );
      entry.subs.set(groupKey, sortedObjects);
    });

    const result: GlobalLibraryParentGroup[] = [];
    parentMap.forEach((value, parentKey) => {
      const subcategories = Array.from(value.subs.entries())
        .map(([groupKey, groupObjects]) => ({
          groupKey,
          displayName: formatGroupLabel(groupKey),
          objects: groupObjects,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      result.push({
        parentKey,
        displayName: value.displayName,
        subcategories,
        objectCount: subcategories.reduce((sum, sub) => sum + sub.objects.length, 0),
      });
    });

    return result.sort((a, b) => {
      if (a.parentKey === OTHER_PARENT_KEY) return 1;
      if (b.parentKey === OTHER_PARENT_KEY) return -1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [globalCategoryGroups, objectCategoryById, parentCategoryById, objectCategoryNameMap]);

  const groupKeyToParentKey = useMemo(() => {
    const map = new Map<string, string>();
    globalLibraryParentGroups.forEach(parent => {
      parent.subcategories.forEach(sub => {
        map.set(sub.groupKey, parent.parentKey);
      });
    });
    return map;
  }, [globalLibraryParentGroups]);

  const objectMatchesGlobalLibrarySearch = (
    obj: { displayName?: string; label?: string; name?: string; id?: string; description?: string },
    groupKey: string,
    searchTerm: string
  ) => {
    const term = searchTerm.toLowerCase();
    const display = getObjectDisplayName(obj).toLowerCase();
    const identifier = (obj.name || obj.id || '').toLowerCase();
    const description = (obj.description || '').toLowerCase();
    const subcategoryName = formatGroupLabel(groupKey).toLowerCase();
    return (
      display.includes(term) ||
      identifier.includes(term) ||
      description.includes(term) ||
      subcategoryName.includes(term)
    );
  };

  // Keyboard shortcuts and global mouse events
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key;
      const isDigit = key >= '0' && key <= '9';
      const isLetter = key.length === 1 && ((key >= 'a' && key <= 'z') || (key >= 'A' && key <= 'Z'));
      if (isDigit || isLetter) {
        const normalizedKey = isLetter ? key.toLowerCase() : key;

        // Featured objects (global doc ids) — digits and letters
        const featuredWithHotkey = currentSchema?.featuredObjects?.find(
          (f: any) => f?.hotkey && String(f.hotkey).toLowerCase() === normalizedKey
        );
        if (featuredWithHotkey?.objectId) {
          setSelectedCategory(featuredWithHotkey.objectId);
        } else if (isDigit && currentSchema?.classes) {
          // Handwriting / legacy classes — digits only, unchanged behavior
          const categoryWithHotkey = currentSchema.classes.find(
            (cat: any) => cat.hotkey === key
          );
          if (categoryWithHotkey) {
            setSelectedCategory(categoryWithHotkey.id);
          }
        }
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedCategory('');
        setSelectedBox(null);
      }
      
      // Delete key to remove selected box
      if (e.key === 'Delete' && selectedBox) {
        // Save to undo history
        setUndoHistory(prev => [...prev, [...boxesRef.current]]);
        
        const newBoxes = boxesRef.current.filter(b => b.id !== selectedBox.id);
        boxesRef.current = newBoxes;
        setBoxes(newBoxes);
        setSelectedBox(null);
        drawCanvas(newBoxes);
      }
    };

    // Global mouse up handler to complete drawing even if mouse leaves canvas
    const handleGlobalMouseUp = (e: MouseEvent) => {
      if (isDrawing && startPoint && selectedCategory) {
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          // Calculate mouse position relative to canvas in CSS pixels
          let x = (e.clientX - rect.left);
          let y = (e.clientY - rect.top);
          
          // Clamp to canvas boundaries (CSS pixels)
          x = Math.max(0, Math.min(canvas.clientWidth, x));
          y = Math.max(0, Math.min(canvas.clientHeight, y));
          
          const newBox = prefillNewBox({
            id: `box-${Date.now()}`,
            x: Math.min(startPoint.x, x),
            y: Math.min(startPoint.y, y),
            width: Math.abs(x - startPoint.x),
            height: Math.abs(y - startPoint.y),
            categoryId: selectedCategory,
            attributes: {}
          });
          
          // Only add if it's a valid box (minimum size)
          if (newBox.width > 10 && newBox.height > 10) {
            // Save to undo history
            setUndoHistory(prev => [...prev, [...boxesRef.current]]);
            
            // Update ref immediately for drawing
            const newBoxes = [...boxesRef.current, newBox];
            boxesRef.current = newBoxes;
            
            // Update state
            setBoxes(newBoxes);
            
            // Draw immediately with the new boxes
            drawCanvas(newBoxes);
          } else {
            // Still need to redraw to clear draft box
            setIsDrawing(false);
            setStartPoint(null);
            setDraftBox(null);
            drawCanvas();
          }
        }
        
        setIsDrawing(false);
        setStartPoint(null);
        setDraftBox(null);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [currentSchema, selectedBox, isDrawing, startPoint, selectedCategory, derivedFromClassId, currentTask, currentSubmission, labelAttributes]);

  // Redraw canvas when hide state changes
  useEffect(() => {
    if (currentSubmission && image) {
      drawCanvas();
    }
  }, [hideAllLabels, hiddenBoxIds, currentSubmission, image]);

  // Sync boxesRef with boxes state
  useEffect(() => {
    boxesRef.current = boxes;
  }, [boxes]);

  // Redraw canvas when boxes state changes (safety net)
  useEffect(() => {
    if (image && !isDrawing) {
      drawCanvas();
    }
  }, [boxes, image, selectedBox, isDrawing]);

  const resizeCanvasForImage = (imgEl: HTMLImageElement, attempt = 0) => {
    if (!canvasRef.current || !imgEl) return;
        const canvas = canvasRef.current;
    const container = canvasContainerRef.current || canvas.parentElement;
    if (!container) return;
    
    const maxWidth = Math.max(container.clientWidth - 8, 100);
    const maxHeight = Math.max(container.clientHeight - 8, 100);
    
    if ((maxWidth <= 100 || maxHeight <= 100) && attempt < 5) {
      requestAnimationFrame(() => resizeCanvasForImage(imgEl, attempt + 1));
      return;
    }
    
    const scaleX = maxWidth / imgEl.width;
    const scaleY = maxHeight / imgEl.height;
          const scale = Math.min(scaleX, scaleY);
    const displayWidth = Math.floor(imgEl.width * scale);
    const displayHeight = Math.floor(imgEl.height * scale);
          const dpr = window.devicePixelRatio || 1;
          canvas.style.width = `${displayWidth}px`;
          canvas.style.height = `${displayHeight}px`;
          canvas.width = Math.floor(displayWidth * dpr);
          canvas.height = Math.floor(displayHeight * dpr);
          drawCanvas();
  };
  
  // Handle window resize to recalculate canvas size
  useEffect(() => {
    const handleResize = () => {
      if (image) {
        resizeCanvasForImage(image);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [image]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load tasks
        const tasksSnapshot = await getDocs(collection(db, 'tasks'));
        const tasksData: Record<string, Task> = {};
        tasksSnapshot.docs.forEach(doc => {
          tasksData[doc.id] = { id: doc.id, ...doc.data() } as Task;
        });
        setTasks(tasksData);
        console.log(`📊 Loaded ${Object.keys(tasksData).length} tasks`);
        
        // Load schemas
        const schemasSnapshot = await getDocs(collection(db, 'labelSchemas'));
        const schemasData: Record<string, any> = {};
        schemasSnapshot.docs.forEach(doc => {
          const data = doc.data();
          schemasData[doc.id] = { id: doc.id, ...data };
          console.log(`📋 [WorkbenchEfficient] Schema ${doc.id}:`, {
            name: data.name,
            classes: data.classes?.length || 0,
            scope: data.scope,
            archived: data.archived
          });
        });
        setSchemas(schemasData);
        console.log(`📊 [WorkbenchEfficient] Loaded ${Object.keys(schemasData).length} schemas`);
        
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Set up real-time listener for schema changes
  useEffect(() => {
    const unsubscribeSchemas = onSnapshot(
      collection(db, 'labelSchemas'),
      (snapshot) => {
        const schemasData: Record<string, any> = {};
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          schemasData[doc.id] = { id: doc.id, ...data };
          console.log(`📋 [WorkbenchEfficient] Schema updated ${doc.id}:`, {
            name: data.name,
            classes: data.classes?.length || 0,
            scope: data.scope,
            archived: data.archived
          });
        });
        setSchemas(schemasData);
        console.log(`📊 [WorkbenchEfficient] Updated ${Object.keys(schemasData).length} schemas`);
        
        // If we have a current task, reload its schema
        if (currentTask?.detection?.schemaRef?.id) {
          const updatedSchema = schemasData[currentTask.detection.schemaRef.id];
          if (updatedSchema) {
            console.log('🔄 [WorkbenchEfficient] Reloading current schema with updated data');
            setCurrentSchema(updatedSchema);
          }
        }
      }
    );

    return () => {
      unsubscribeSchemas();
    };
  }, [currentTask]);

  // Load submissions
  useEffect(() => {
    console.log('🔍 [WorkbenchEfficient] Setting up submissions listener with', Object.keys(tasks).length, 'tasks loaded');
    
    const unsubscribe = onSnapshot(
      query(collection(db, 'submissions'), where('status', 'in', ['approved', 'needs_relabeling'])),
      (snapshot) => {
        console.log(`📊 [WorkbenchEfficient] Received ${snapshot.docs.length} submissions with status 'approved' or 'needs_relabeling'`);
        
        const submissionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Submission[];
        
        // Log all received submissions for debugging
        submissionsData.forEach(sub => {
          const task = tasks[sub.taskId];
          console.log(`📋 [WorkbenchEfficient] Received submission:`, {
            id: sub.id.slice(-8),
            taskId: sub.taskId,
            taskTitle: task?.title || 'Task not loaded',
            status: sub.status,
            createdAt: sub.createdAt,
            taskHasDetection: task ? (task.detection?.enabled || false) : 'Task not found',
            taskHasSchema: task ? (!!task.detection?.schemaRef?.id || false) : 'Task not found',
            willAppearInQueue: task ? (task.detection?.enabled && task.detection?.schemaRef?.id) : false
          });
        });
        
        // Filter to only show submissions that have detection schemas and are not in QA Review
        const availableSubmissions = submissionsData.filter(submission => {
          // Exclude submissions that are in QA Review
          if (submission.qaReviewStatus === 'pending' || submission.qaReviewStatus === 'saved_for_later') {
            return false;
          }
          const task = tasks[submission.taskId];
          const hasDetection = task?.detection?.enabled && task?.detection?.schemaRef?.id;
          
          // Enhanced logging for debugging
          if (!task) {
            console.warn(`⚠️ [WorkbenchEfficient] Submission ${submission.id.slice(-8)}: Task ${submission.taskId} not found in tasks map`);
            return false;
          }
          
          if (!hasDetection) {
            console.warn(`⚠️ [WorkbenchEfficient] Submission ${submission.id.slice(-8)} filtered out:`, {
              submissionId: submission.id,
              taskId: submission.taskId,
              taskTitle: task.title,
              status: submission.status,
              detectionEnabled: task.detection?.enabled,
              hasSchema: !!task.detection?.schemaRef?.id,
              schemaId: task.detection?.schemaRef?.id,
              reason: !task.detection?.enabled ? 'Detection not enabled' : 'No schema assigned'
            });
            return false;
          }
          
          console.log(`✅ [WorkbenchEfficient] Submission ${submission.id.slice(-8)} included:`, {
            submissionId: submission.id,
            taskId: submission.taskId,
            taskTitle: task.title,
            status: submission.status,
            schemaId: task.detection?.schemaRef?.id
          });
          
          return true;
        });
        
        // Sort by creation date (oldest first)
        availableSubmissions.sort((a, b) => {
          const aTime = typeof a.createdAt === 'number' ? a.createdAt : 
                       (a.createdAt && typeof a.createdAt === 'object' && 'toMillis' in a.createdAt) ? 
                       (a.createdAt as any).toMillis() : 0;
          const bTime = typeof b.createdAt === 'number' ? b.createdAt : 
                       (b.createdAt && typeof b.createdAt === 'object' && 'toMillis' in b.createdAt) ? 
                       (b.createdAt as any).toMillis() : 0;
          return aTime - bTime;
        });
        
        setSubmissions(availableSubmissions);
        
        // Auto-load first submission if none is selected
        if (availableSubmissions.length > 0 && !currentSubmission) {
          loadSubmission(availableSubmissions[0]);
        }
      },
      (error) => {
        console.error('Error loading submissions:', error);
        setError('Failed to load submissions');
      }
    );

    return () => unsubscribe();
  }, [tasks, currentSubmission]);

  const loadSubmission = async (submission: Submission, pushHistory: boolean = true) => {
    try {
      if (pushHistory && currentSubmission) {
        setHistory(prev => [...prev, currentSubmission]);
      }
      setCurrentSubmission(submission);
      
      const task = tasks[submission.taskId];
      setCurrentTask(task);
      const derivedFromClassIdMap = computeDerivedFromClassId(task, submission);
      setDerivedFromClassId(derivedFromClassIdMap);
      
      if (task?.detection?.schemaRef?.id) {
        const schema = schemas[task.detection.schemaRef.id];
        console.log('🔍 [WorkbenchEfficient] Loading schema:', {
          schemaId: task.detection.schemaRef.id,
          schema: schema,
          hasClasses: schema?.classes?.length || 0
        });
        setCurrentSchema(schema);
        const imageAttrDefs = resolveAttributeDefinitions(schema?.imageAttributes, labelAttributes);
        setImageAttributeValues(
          prefillImageAttributeValues(
            submission.imageAttributes || {},
            imageAttrDefs,
            task,
            submission,
            derivedFromClassIdMap
          )
        );
      } else {
        console.log('❌ [WorkbenchEfficient] No detection schema found for task:', {
          taskId: task?.id,
          detection: task?.detection
        });
        setImageAttributeValues({});
      }
      
      // ─────────────────────────────────────────────────────────────────────────
      // Multi-photo handling
      // ─────────────────────────────────────────────────────────────────────────
      const isMulti = isMultiPhotoSubmission(submission);
      const requiredRoles = getRequiredRoles(submission);
      
      console.log('📷 [WorkbenchEfficient] Loading submission:', {
        id: submission.id,
        isMultiPhoto: isMulti,
        requiredRoles,
        hasLabelsByRole: !!submission.labelsByRole,
        hasLabels: !!(submission.labels?.length)
      });
      
      // Reset multi-photo state
      setActiveRole('primary');
      const firstStepKey = getRequiredSteps(submission)[0] || 'primary';
      setActiveStepKey(firstStepKey);
      
      if (isMulti) {
        // ─────────────────────────────────────────────────────────────────────
        // MULTI-PHOTO: Load labels for all roles from labelsByRole or initialize empty
        // ─────────────────────────────────────────────────────────────────────
        const newBoxesByRole: Partial<Record<MediaRole, BoundingBox[]>> = {};
        const newFlaggedByRole: Partial<Record<MediaRole, LabelData[]>> = {};
        const newSavedRoles = new Set<MediaRole>();
        
        // Check which roles already have saved labels (using stepKeys internally)
        const stepKeys = getRequiredSteps(submission);
        for (const role of requiredRoles) {
          // Find corresponding stepKey for this role
          const mediaItem = submission.media?.find((m: any) => m.role === role);
          const stepKey = stepKeyFromMedia(mediaItem);
          
          // Use helper to read labels with fallback priority
          const roleLabels = getLabelsForStep(submission, stepKey);
          if (roleLabels.length > 0) {
            newSavedRoles.add(role);
          }
          // Initialize with empty arrays - actual boxes will be populated after image loads
          newBoxesByRole[role] = [];
          newFlaggedByRole[role] = roleLabels.filter((l: any) => l.flagged === true);
        }
        
        setBoxesByRole(newBoxesByRole);
        setFlaggedLabelsByRole(newFlaggedByRole);
        setUndoHistoryByRole({});
        setSavedRoles(newSavedRoles);
        
        // Load primary role image first
        const primaryUrl = getImageUrlForRole(submission, 'primary');
        // Use helper to read labels with fallback priority
        const primaryMediaItem = submission.media?.find((m: any) => m.role === 'primary');
        const primaryStepKey = stepKeyFromMedia(primaryMediaItem);
        const primaryLabelsAll = getLabelsForStep(submission, primaryStepKey);
        const primaryLabels = primaryLabelsAll.filter((l: any) => !l.flagged);
        const primaryFlagged = primaryLabelsAll.filter((l: any) => l.flagged === true);
        
        setFlaggedLabels(primaryFlagged);
        
        if (primaryUrl) {
          const schema = task?.detection?.schemaRef?.id ? schemas[task.detection.schemaRef.id] : null;
          const prefillContext = buildPrefillContext(
            submission,
            task,
            schema,
            derivedFromClassIdMap
          );
          loadImageAndBoxes(primaryUrl, primaryLabels, (loadedBoxes) => {
            // Store in boxesByRole for primary
            setBoxesByRole(prev => ({ ...prev, primary: loadedBoxes }));
          }, prefillContext);
        } else {
          boxesRef.current = [];
          setBoxes([]);
        }
        
      } else {
        // ─────────────────────────────────────────────────────────────────────
        // SINGLE-PHOTO: Use legacy labels[] field (unchanged behavior)
        // ─────────────────────────────────────────────────────────────────────
        setBoxesByRole({});
        setFlaggedLabelsByRole({});
        setUndoHistoryByRole({});
        setSavedRoles(new Set());
        
      const regularLabels = (submission.labels || []).filter((label: any) => !label.flagged);
      const flagged = (submission.labels || []).filter((label: any) => label.flagged === true);
      
      setFlaggedLabels(flagged);
      
        // Load image and then load boxes (existing behavior)
      const imageUrl = submission.imageUrl || submission.mediaUrl;
      if (imageUrl) {
          const schema = task?.detection?.schemaRef?.id ? schemas[task.detection.schemaRef.id] : null;
          const prefillContext = buildPrefillContext(
            submission,
            task,
            schema,
            derivedFromClassIdMap
          );
          loadImageAndBoxes(imageUrl, regularLabels, undefined, prefillContext);
        } else {
          boxesRef.current = [];
          setBoxes([]);
        }
      }
      
      setSelectedBox(null);
      setHideAllLabels(false);
      setHiddenBoxIds(new Set());
      setUndoHistory([]);
      
    } catch (err) {
      console.error('Error loading submission:', err);
      setError('Failed to load submission');
    }
  };
  
  // Helper function to load image and convert labels to boxes
  const loadImageAndBoxes = (
    imageUrl: string, 
    labels: LabelData[], 
    onBoxesLoaded?: (boxes: BoundingBox[]) => void,
    prefillContext?: {
      schema: any;
      task: Task | null;
      submission: Submission;
      labelAttributes: LabelAttribute[];
      getCategoryById: (id: string) => any;
      derivedFromClassId: Record<string, any>;
    }
  ) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
    
    const handleImageLoad = (loadedImg: HTMLImageElement) => {
          console.log(`✅ [WorkbenchEfficient] Image loaded successfully:`, {
        width: loadedImg.width,
        height: loadedImg.height,
        src: loadedImg.src
          });
      setImage(loadedImg);
      
          requestAnimationFrame(() => {
        resizeCanvasForImage(loadedImg);
            setTimeout(() => {
            const canvas = canvasRef.current;
          if (canvas && loadedImg) {
                const displayW = canvas.clientWidth;
                const displayH = canvas.clientHeight;
            const scaleXToDisplay = loadedImg.width > 0 ? displayW / loadedImg.width : 1;
            const scaleYToDisplay = loadedImg.height > 0 ? displayH / loadedImg.height : 1;
                const scale = Math.min(scaleXToDisplay, scaleYToDisplay);
                
            const newBoxes: BoundingBox[] = labels.map((label: any, idx: number) => {
                  const [x1, y1, x2, y2] = label.bbox;
                  return {
                    id: `box-${Date.now()}-${idx}`,
                    x: x1 * scale,
                    y: y1 * scale,
                    width: (x2 - x1) * scale,
                    height: (y2 - y1) * scale,
                    categoryId: label.categoryId,
                    attributes: label.attributes || {},
                    sourceLabel: label,
                  };
                });

            const prefilledBoxes = prefillContext
              ? newBoxes.map((box) => {
                  const attributeDefs = getInstanceAttributeDefsForBox(
                    box,
                    prefillContext.schema,
                    prefillContext.labelAttributes,
                    prefillContext.getCategoryById
                  );
                  return prefillBoxAttributeValues(
                    box,
                    attributeDefs,
                    prefillContext.task,
                    prefillContext.submission,
                    prefillContext.derivedFromClassId
                  );
                })
              : newBoxes;
                
                boxesRef.current = prefilledBoxes;
                setBoxes(prefilledBoxes);
                drawCanvas(prefilledBoxes);
            
            // Callback for multi-photo to store in boxesByRole
            if (onBoxesLoaded) {
              onBoxesLoaded(prefilledBoxes);
            }
              }
            }, 100);
          });
        };
    
    img.onload = () => handleImageLoad(img);
        img.onerror = () => {
          console.error('❌ [WorkbenchEfficient] Image load error, retrying without CORS...');
          const imgRetry = new Image();
      imgRetry.onload = () => handleImageLoad(imgRetry);
          imgRetry.onerror = () => {
            console.error('❌ [WorkbenchEfficient] Image load failed even without CORS');
            setError(`Failed to load image: ${imageUrl}. This may be a CORS or permissions issue.`);
          };
          imgRetry.src = imageUrl;
        };
        img.src = imageUrl;
  };

  const drawCanvas = (boxesToDraw?: BoundingBox[]) => {
    if (!canvasRef.current || !image) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    // Ensure transform maps CSS pixels to backing pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    
    // Clear canvas in CSS pixel coords
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    
    // Draw image scaled to fit canvas (contain behavior already sized)
    ctx.drawImage(image, 0, 0, displayWidth, displayHeight);
    
    // Use provided boxes or fall back to state
    const boxesToRender = boxesToDraw ?? boxes;
    
    // Filter out boxes if hideAllLabels is true, or if individual boxes are hidden
    const visibleBoxes = hideAllLabels ? [] : boxesToRender.filter(box => !hiddenBoxIds.has(box.id));
    
    // Draw boxes (thicker for visibility, with fill to keep them highlighted)
    visibleBoxes.forEach(box => {
      const isSelected = selectedBox?.id === box.id;
      const strokeColor = isSelected ? '#ef4444' : '#1e40af'; // red for selected, blue-900 for others
      const fillColor = isSelected ? 'rgba(239, 68, 68, 0.15)' : 'rgba(30, 64, 175, 0.15)'; // subtle fill
      
      // Draw fill first (behind stroke)
      ctx.fillStyle = fillColor;
      ctx.fillRect(box.x, box.y, box.width, box.height);
      
      // Draw stroke
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(box.x, box.y, box.width, box.height);
      
      // Draw category label with background
      const categoryName = getCategoryName(box.categoryId);
      ctx.font = 'bold 12px Arial';
      const textMetrics = ctx.measureText(categoryName);
      const textWidth = textMetrics.width;
      const textHeight = 16;
      
      // Draw label background
      ctx.fillStyle = isSelected ? 'rgba(239, 68, 68, 0.9)' : 'rgba(30, 64, 175, 0.9)';
      ctx.fillRect(box.x + 2, box.y - textHeight - 2, textWidth + 8, textHeight);
      
      // Draw label text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(categoryName, box.x + 6, box.y - 6);
    });

    // Draw draft (preview) box if present
    if (draftBox) {
      ctx.save();
      ctx.strokeStyle = '#2563eb'; // blue-600
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.strokeRect(draftBox.x, draftBox.y, draftBox.width, draftBox.height);
      // Light fill to increase visibility
      ctx.fillStyle = 'rgba(37, 99, 235, 0.12)';
      ctx.fillRect(draftBox.x, draftBox.y, draftBox.width, draftBox.height);
      ctx.restore();
    }
  };

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!selectedCategory) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Use CSS pixel coordinates; backing store scaling handled via ctx transform
    const x = (e.clientX - rect.left);
    const y = (e.clientY - rect.top);
    
    setIsDrawing(true);
    setStartPoint({ x, y });
    setDraftBox({ x, y, width: 0, height: 0 });
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPoint) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Work in CSS pixels
    let x = (e.clientX - rect.left);
    let y = (e.clientY - rect.top);
    x = Math.max(0, Math.min(canvas.clientWidth, x));
    y = Math.max(0, Math.min(canvas.clientHeight, y));

    const preview = {
      x: Math.min(startPoint.x, x),
      y: Math.min(startPoint.y, y),
      width: Math.abs(x - startPoint.x),
      height: Math.abs(y - startPoint.y)
    };

    // Persist for mouseup, but render immediately without waiting for state flush
    setDraftBox(preview);

    // Redraw existing content
    drawCanvas();

    // Draw the preview box directly so it shows instantly
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.save();
      ctx.strokeStyle = '#2563eb'; // blue-600
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.strokeRect(preview.x, preview.y, preview.width, preview.height);
      ctx.fillStyle = 'rgba(37, 99, 235, 0.12)';
      ctx.fillRect(preview.x, preview.y, preview.width, preview.height);
      ctx.restore();
    }
  };

  const handleCanvasMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // The global mouse up handler will take care of completing the box
    // This handler just prevents the default behavior
    e.preventDefault();
  };

  const handleCanvasMouseLeave = () => {
    if (isDrawing && startPoint && selectedCategory) {
      // Complete the box when mouse leaves canvas
      const canvas = canvasRef.current;
      if (canvas) {
        // Use the edge of the canvas (CSS pixels) as the end point
        const x = canvas.clientWidth;
        const y = canvas.clientHeight;
        
        const newBox = prefillNewBox({
          id: `box-${Date.now()}`,
          x: Math.min(startPoint.x, x),
          y: Math.min(startPoint.y, y),
          width: Math.abs(x - startPoint.x),
          height: Math.abs(y - startPoint.y),
          categoryId: selectedCategory,
          attributes: {}
        });
        
        // Only add if it's a valid box (minimum size)
        if (newBox.width > 10 && newBox.height > 10) {
          // Update ref immediately for drawing
          const newBoxes = [...boxesRef.current, newBox];
          boxesRef.current = newBoxes;
          
          // Update state
          setBoxes(newBoxes);
          
          // Draw immediately with the new boxes
          drawCanvas(newBoxes);
        } else {
          // Still need to redraw to clear draft box
          setIsDrawing(false);
          setStartPoint(null);
          setDraftBox(null);
          drawCanvas();
        }
      }
      
      setIsDrawing(false);
      setStartPoint(null);
      setDraftBox(null);
    }
  };

  const handleSave = async () => {
    if (!currentSubmission) return;
    
    try {
      setSaving(true);
      console.log('🔍 [WorkbenchEfficient] Starting save process...');
      
      // Compute scale back to original image pixels
      const canvas = canvasRef.current;
      if (!image || !canvas) {
        console.error('❌ [WorkbenchEfficient] Image or canvas not loaded');
        alert('Image not loaded');
        setSaving(false);
        return;
      }
      
      // Determine scale from CSS display size, not backing store (DPR-safe)
      const displayW = canvas.clientWidth;
      const displayH = canvas.clientHeight;
      const scaleXToDisplay = image.width > 0 ? displayW / image.width : 1;
      const scaleYToDisplay = image.height > 0 ? displayH / image.height : 1;
      const scale = Math.min(scaleXToDisplay, scaleYToDisplay);
      console.log('📐 [WorkbenchEfficient] Scale calculation:', {
        canvasWidthCSS: displayW,
        canvasHeightCSS: displayH,
        imageWidth: image.width,
        imageHeight: image.height,
        scale: scale
      });

      // Check if there are flagged labels - cannot save if flags exist
      if (flaggedLabels.length > 0) {
        alert('Cannot save submission with flagged labels. Please remove all flagged labels first.');
        setSaving(false);
        return;
      }

      const attributeValidation = validateRequiredAttributes();
      if (!attributeValidation.ok) {
        showAttributeValidationAlert(attributeValidation.messages);
        setSaving(false);
        return;
      }

      // Convert current boxes to labels format
      const labelsForRole = boxes.map(b => {
        const x1 = Math.round(b.x / scale);
        const y1 = Math.round(b.y / scale);
        const x2 = Math.round((b.x + b.width) / scale);
        const y2 = Math.round((b.y + b.height) / scale);
        return {
          categoryId: b.categoryId,
          bbox: [x1, y1, x2, y2],
          attributes: b.attributes || {}
        };
      });

      const auth = getAuth();
      const uid = auth.currentUser?.uid || 'unknown';
      console.log('👤 [WorkbenchEfficient] User ID:', uid);

      const isMulti = isMultiPhotoSubmission(currentSubmission);
      
      if (!isMulti) {
        // ─────────────────────────────────────────────────────────────────────
        // SINGLE-PHOTO: Keep labels[] unchanged, also add labelsByStep['primary']
        // ─────────────────────────────────────────────────────────────────────
        const labelsForSave = boxes.map((b) => serializeBoxToLabel(b, scale));
        console.log('📦 [WorkbenchEfficient] Single-photo save - labels:', labelsForSave);

        // Derive stepKey for single-photo (should be 'primary' for legacy)
        const primaryMediaItem = currentSubmission.media?.find((m: any) => m.role === 'primary') || 
                                 (currentSubmission.media && currentSubmission.media.length > 0 ? currentSubmission.media[0] : null);
        const primaryStepKey = stepKeyFromMedia(primaryMediaItem);

      const submissionData = {
          labels: labelsForSave,
          labelsByStep: {
            [primaryStepKey]: labelsForSave
          },
        imageAttributes: imageAttributeValues,
        status: 'labeled',
        labeledAt: serverTimestamp(),
        labeledBy: uid
      };

      console.log('💾 [WorkbenchEfficient] Saving to submission:', currentSubmission.id, submissionData);
      await setDoc(doc(db, 'submissions', currentSubmission.id), submissionData, { merge: true });
      console.log('✅ [WorkbenchEfficient] Successfully saved labels');

      // Auto-advance to next item in queue
        advanceToNextSubmission('Objects saved. Moving to next in queue.', 'Objects saved. No more items in queue.');
      
      } else {
        // ─────────────────────────────────────────────────────────────────────
        // MULTI-PHOTO: Dual-write to labelsByRole (legacy) and labelsByStep (new)
        // Only set status when all steps are labeled
        // ─────────────────────────────────────────────────────────────────────
        // CRITICAL: Use activeStepKey (for step-based) or derive from activeRole (legacy)
        const currentStepKey = shouldUseStepKeyTabs 
          ? activeStepKey 
          : stepKeyFromMedia(currentSubmission.media?.find((m: any) => m.role === activeRole));
        
        // Get required stepKeys from submission.media[] (NOT from task.steps)
        const requiredStepKeys = getRequiredSteps(currentSubmission);
        
        console.log('📦 [WorkbenchEfficient] Multi-photo save:', {
          activeRole,
          currentStepKey,
          requiredStepKeys,
          labelsForRole: labelsForRole.length
        });
        
        // Get existing labelsByRole and labelsByStep from submission
        const existingLabelsByRole = currentSubmission.labelsByRole || {};
        const existingLabelsByStep = currentSubmission.labelsByStep || {};
        
        // First, persist any other role's boxes that are in memory but not yet saved
        const updatedBoxesByRole = {
          ...boxesByRole,
          [activeRole]: [...boxesRef.current]
        };
        
        // Build next labelsByRole with updated active role (legacy format)
        const nextLabelsByRole: Partial<Record<MediaRole, any[]>> = {
          ...existingLabelsByRole,
          [activeRole]: labelsForRole
        };
        
        // Build next labelsByStep with updated active stepKey (new format)
        const nextLabelsByStep: Partial<Record<string, any[]>> = {
          ...existingLabelsByStep,
          [currentStepKey]: labelsForRole
        };
        
        // Determine if all required stepKeys have labels (stepKey-based completion)
        const allStepsLabeled = requiredStepKeys.every(stepKey => {
          // Check labelsByStep first, then fallback to labelsByRole via getLabelsForStep
          const stepLabels = getLabelsForStep(
            { ...currentSubmission, labelsByStep: nextLabelsByStep, labelsByRole: nextLabelsByRole },
            stepKey
          );
          return Array.isArray(stepLabels) && stepLabels.length > 0;
        });
        
        console.log('🔍 [WorkbenchEfficient] Multi-photo completion check (stepKey-based):', {
          requiredStepKeys,
          nextLabelsByStep: Object.fromEntries(
            requiredStepKeys.map(sk => [sk, (nextLabelsByStep[sk] || []).length])
          ),
          allStepsLabeled
        });
        
        // Build submission data with dual-write
        const submissionData: any = {
          labelsByRole: nextLabelsByRole,  // Legacy format
          labelsByStep: nextLabelsByStep,   // New format (stepKey-based)
          imageAttributes: imageAttributeValues,
          labeledAt: serverTimestamp(),
          labeledBy: uid
        };
        
        // Only set status to 'labeled' when ALL steps are labeled
        if (allStepsLabeled) {
          submissionData.status = 'labeled';
          console.log('✅ [WorkbenchEfficient] All steps labeled - setting status to "labeled"');
        } else {
          console.log('⏳ [WorkbenchEfficient] Not all steps labeled yet - keeping status unchanged');
        }
        
        console.log('💾 [WorkbenchEfficient] Saving multi-photo submission:', currentSubmission.id, submissionData);
        await setDoc(doc(db, 'submissions', currentSubmission.id), submissionData, { merge: true });
        
        // Update local state to reflect save
        setBoxesByRole(updatedBoxesByRole);
        setSavedRoles(prev => new Set([...prev, activeRole]));
        
        // Update the current submission in memory
        setCurrentSubmission(prev => prev ? {
          ...prev,
          labelsByRole: nextLabelsByRole,
          labelsByStep: nextLabelsByStep
        } : prev);
        
        if (allStepsLabeled) {
          // All done - advance to next submission
          advanceToNextSubmission(
            'All images labeled. Moving to next in queue.',
            'All images labeled. No more items in queue.'
          );
        } else {
          // Find next unlabeled stepKey and switch to its role
          const nextUnlabeledStepKey = requiredStepKeys.find(stepKey => {
            const stepLabels = getLabelsForStep(
              { ...currentSubmission, labelsByStep: nextLabelsByStep, labelsByRole: nextLabelsByRole },
              stepKey
            );
            return !Array.isArray(stepLabels) || stepLabels.length === 0;
          });
          
          if (nextUnlabeledStepKey) {
            // Map stepKey back to role for UI
            const nextUnlabeledRole = stepKeyToRole(nextUnlabeledStepKey, currentSubmission);
            if (nextUnlabeledRole && nextUnlabeledRole !== activeRole) {
              alert(`${getRoleDisplayLabel(activeRole, currentSubmission)} saved. Switching to ${getRoleDisplayLabel(nextUnlabeledRole, currentSubmission)}.`);
              switchRole(nextUnlabeledRole);
            } else {
              alert(`${getRoleDisplayLabel(activeRole, currentSubmission)} labels saved.`);
            }
          } else {
            alert(`${getRoleDisplayLabel(activeRole, currentSubmission)} labels saved.`);
          }
        }
      }

      // Reset drawing state
      setSelectedBox(null);
      setDraftBox(null);
      
    } catch (err) {
      console.error('❌ [WorkbenchEfficient] Error saving:', err);
      const error = err as any;
      console.error('❌ [WorkbenchEfficient] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Failed to save objects: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Helper to advance to next submission in queue
  const advanceToNextSubmission = (successMessage: string, emptyQueueMessage: string) => {
    const currentIndex = filteredSubmissions.findIndex(s => s.id === currentSubmission?.id);
    const nextSubmission = filteredSubmissions[currentIndex + 1];
    
    if (nextSubmission) {
      loadSubmission(nextSubmission, false);
      alert(successMessage);
    } else {
      setCurrentSubmission(null);
      setImage(null);
      boxesRef.current = [];
      setBoxes([]);
      alert(emptyQueueMessage);
    }
  };

  const handleQAClick = () => {
    setShowQAModal(true);
    setQaReason("");
  };

  const handleQAReasonChange = (reason: string) => {
    setQaReason(reason);
  };

  const handleQASubmit = async () => {
    if (!qaReason.trim()) {
      alert("Please select a QA Review reason.");
      return;
    }
    
    if (!currentSubmission) return;
    
    try {
      setSaving(true);
      
      // Save current labels if any
      const canvas = canvasRef.current;
      if (image && canvas && boxes.length > 0) {
        const attributeValidation = validateRequiredAttributes();
        if (!attributeValidation.ok) {
          showAttributeValidationAlert(attributeValidation.messages);
          setSaving(false);
          return;
        }

        const displayW = canvas.clientWidth;
        const displayH = canvas.clientHeight;
        const scaleXToDisplay = image.width > 0 ? displayW / image.width : 1;
        const scaleYToDisplay = image.height > 0 ? displayH / image.height : 1;
        const scale = Math.min(scaleXToDisplay, scaleYToDisplay);

        const labels = boxes.map((b) => serializeBoxToLabel(b, scale));

        const auth = getAuth();
        const uid = auth.currentUser?.uid || 'unknown';
        
        await setDoc(doc(db, 'submissions', currentSubmission.id), {
          labels,
          status: 'approved', // Keep as approved (user already paid)
          qaReviewStatus: 'pending',
          qaReviewReason: qaReason,
          qaReviewSentAt: serverTimestamp(),
          qaReviewSentBy: uid,
          labeledAt: serverTimestamp(),
          labeledBy: uid
        }, { merge: true });
      } else {
        // No labels, just mark for QA review
        const auth = getAuth();
        const uid = auth.currentUser?.uid || 'unknown';
        
        await setDoc(doc(db, 'submissions', currentSubmission.id), {
          status: 'approved',
          qaReviewStatus: 'pending',
          qaReviewReason: qaReason,
          qaReviewSentAt: serverTimestamp(),
          qaReviewSentBy: uid
        }, { merge: true });
      }

      // Remove from current queue and move to next
      const currentIndex = submissions.findIndex(s => s.id === currentSubmission.id);
      const nextSubmission = submissions[currentIndex + 1];
      
      // Close modal
      setShowQAModal(false);
      setQaReason("");
      
      if (nextSubmission) {
        loadSubmission(nextSubmission, false);
        alert('Submission sent to QA Review. Moving to next in queue.');
      } else {
        setCurrentSubmission(null);
        setImage(null);
        boxesRef.current = [];
        setBoxes([]);
        alert('Submission sent to QA Review. No more items in queue.');
      }

      setSelectedBox(null);
      setDraftBox(null);
      
    } catch (err) {
      console.error('Error sending to QA Review:', err);
      const error = err as any;
      alert(`Failed to send to QA Review: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleQAModalClose = () => {
    setShowQAModal(false);
    setQaReason("");
  };

  const getTaskName = (taskId: string) => {
    const task = tasks[taskId];
    return task ? task.title : '-';
  };

  // ---- Queue filter accessors ----
  const sCategory = (s: any) => s?.category || '';
  const sSubcategory = (s: any) => s?.subcategory || '';
  const sItemType = (s: any) => s?.itemType || '';

  const uniqueCategories = useMemo(() => {
    const counts = new Map<string, number>();
    submissions.forEach((s: any) => {
      const v = sCategory(s);
      if (v) counts.set(v, (counts.get(v) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [submissions]);

  const uniqueSubcategories = useMemo(() => {
    const pool = filters.category === 'all'
      ? submissions
      : submissions.filter((s: any) => sCategory(s) === filters.category);
    const counts = new Map<string, number>();
    pool.forEach((s: any) => {
      const v = sSubcategory(s);
      if (v) counts.set(v, (counts.get(v) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [submissions, filters.category]);

  const uniqueItemTypes = useMemo(() => {
    const pool = submissions
      .filter((s: any) => filters.category === 'all' || sCategory(s) === filters.category)
      .filter((s: any) => filters.subcategory === 'all' || sSubcategory(s) === filters.subcategory);
    const counts = new Map<string, number>();
    pool.forEach((s: any) => {
      const v = sItemType(s);
      if (v) counts.set(v, (counts.get(v) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [submissions, filters.category, filters.subcategory]);

  const uniqueTasks = useMemo(() => {
    const pool = submissions
      .filter((s: any) => filters.category === 'all' || sCategory(s) === filters.category)
      .filter((s: any) => filters.subcategory === 'all' || sSubcategory(s) === filters.subcategory)
      .filter((s: any) => filters.itemType === 'all' || sItemType(s) === filters.itemType);
    const map = new Map<string, { title: string; count: number }>();
    pool.forEach((s: any) => {
      if (!s.taskId) return;
      const existing = map.get(s.taskId);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(s.taskId, { title: tasks[s.taskId]?.title || s.taskId, count: 1 });
      }
    });
    return [...map.entries()]
      .map(([id, v]) => [id, v.title, v.count] as [string, string, number])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [submissions, tasks, filters.category, filters.subcategory, filters.itemType]);

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((s: any) => {
      if (filters.category !== 'all' && sCategory(s) !== filters.category) return false;
      if (filters.subcategory !== 'all' && sSubcategory(s) !== filters.subcategory) return false;
      if (filters.itemType !== 'all' && sItemType(s) !== filters.itemType) return false;
      if (filters.task !== 'all' && s.taskId !== filters.task) return false;
      return true;
    });
  }, [submissions, filters]);

  // If the current submission is filtered out, move to the first visible one.
  // IMPORTANT: never set currentSubmission to null here — the submissions listener
  // auto-loads an UNFILTERED submission whenever currentSubmission is null, which
  // would fight the filter.
  useEffect(() => {
    if (filteredSubmissions.length === 0) return;
    const stillVisible = currentSubmission && filteredSubmissions.some((s) => s.id === currentSubmission.id);
    if (!stillVisible) {
      loadSubmission(filteredSubmissions[0], false);
    }
  }, [filteredSubmissions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading workbench...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-2">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900 truncate">Annotation Workbench</h1>
              {/* Queue Filters */}
              <div className="mt-3 pr-6 pb-3 border-b border-gray-200 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value, subcategory: 'all', itemType: 'all', task: 'all' }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Categories</option>
                    {uniqueCategories.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Subcategory</label>
                  <select
                    value={filters.subcategory}
                    onChange={(e) => setFilters(prev => ({ ...prev, subcategory: e.target.value, itemType: 'all', task: 'all' }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Subcategories</option>
                    {uniqueSubcategories.map(([s, n]) => <option key={s} value={s}>{s} ({n})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Item Type</label>
                  <select
                    value={filters.itemType}
                    onChange={(e) => setFilters(prev => ({ ...prev, itemType: e.target.value, task: 'all' }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Item Types</option>
                    {uniqueItemTypes.map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Task</label>
                  <select
                    value={filters.task}
                    onChange={(e) => setFilters(prev => ({ ...prev, task: e.target.value }))}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Tasks</option>
                    {uniqueTasks.map(([id, title, n]) => <option key={id} value={id}>{title} ({n})</option>)}
                  </select>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setFilters({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' })}
                    className="w-full px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            {currentSubmission ? (
              <div className="space-y-1 mt-3">
                <div className="flex items-center gap-6 text-lg">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">Task:</span>
                    <span className="text-xl font-semibold text-gray-900">{getTaskName(currentSubmission.taskId)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-base">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">Category:</span>
                    <span className="text-gray-900">{currentSubmission.category || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">Subcategory:</span>
                    <span className="text-gray-900">{(currentSubmission as any).subcategory || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">Item Type:</span>
                    <span className="text-gray-900">{(currentSubmission as any).itemType || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">Language:</span>
                    <span className="text-gray-900">{(currentSubmission as any).contentLanguageLabel || '-'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-base text-gray-600 truncate mt-2">Select a submission to start</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Content - Image and Canvas */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Category Selection and Controls Row */}
          {currentSubmission && image && (
            <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
              {/* Selected Category Indicator */}
              {selectedCategory ? (
                  <>
                  <div className="w-3 h-3 bg-blue-600 rounded-full flex-shrink-0"></div>
                  {(() => {
                      // First try to find in schema classes
                      const schemaCategory = currentSchema?.classes?.find((c: any) => c.id === selectedCategory);
                      if (schemaCategory) {
                        const displayName = schemaCategory.displayName || schemaCategory.label || schemaCategory.name || '';
                        const objectId = schemaCategory.name || schemaCategory.id || selectedCategory;
                    return (
                          <div className="flex items-center space-x-2 min-w-0">
                            {displayName && (
                              <span className="font-medium text-blue-700 truncate">
                                {displayName}
                      </span>
                            )}
                          </div>
                        );
                      }
                      
                      // Then try to find in global categories
                      const globalCategory = getCategoryById(selectedCategory);
                      if (globalCategory) {
                        const displayName = globalCategory.displayName || globalCategory.name || '';
                        return (
                          <div className="flex items-center space-x-2 min-w-0">
                            {displayName && (
                              <span className="font-medium text-blue-700 truncate">
                                {displayName}
                              </span>
                            )}
                </div>
                        );
                      }
                      
                      // Fallback if not found
                      return (
                        <span className="font-medium text-blue-700 truncate">
                          {selectedCategory}
                        </span>
                      );
                    })()}
                  </>
                ) : (
                  <>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                    <span className="text-sm text-gray-600 truncate">Select an object to begin annotation</span>
                  </>
                )}
              </div>
              
              {/* Create Object, Hide Objects, Undo, and QA Review Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowCreateObjectModal(true)}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Object
                </button>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideAllLabels}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setHideAllLabels(newValue);
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Hide Objects</span>
                </label>
                <button
                  onClick={() => {
                    if (boxesRef.current.length > 0) {
                      // Save current state to history before deleting
                      setUndoHistory(prev => [...prev, [...boxesRef.current]]);
                      
                      // Remove the last object (most recently added)
                      const newBoxes = boxesRef.current.slice(0, -1);
                      boxesRef.current = newBoxes;
                      setBoxes(newBoxes);
                      
                      // Clear selected box if it was the one deleted
                      if (selectedBox && newBoxes.length > 0 && !newBoxes.find(b => b.id === selectedBox.id)) {
                        setSelectedBox(null);
                      } else if (newBoxes.length === 0) {
                        setSelectedBox(null);
                      }
                      
                      requestAnimationFrame(() => {
                        drawCanvas(newBoxes);
                      });
                    }
                  }}
                  disabled={boxesRef.current.length === 0}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete the last object added"
                >
                  Undo
                </button>
                <button
                  onClick={handleQAClick}
                  disabled={saving || !currentSubmission}
                  className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                  title="Send to QA Review for correction (user already paid)"
                >
                  {saving ? 'Sending...' : 'QA Review'}
                </button>
              </div>
            </div>
          )}
          
          {currentSubmission && image ? (
            <div className="flex-1 flex bg-gray-100 p-2 min-h-0 gap-2">
              {/* Image/Canvas Area */}
              <div className="flex-1 flex flex-col min-w-0 overflow-auto">
                {/* ─────────────────────────────────────────────────────────────── */}
                {/* Multi-photo Role Toggle */}
                {/* ─────────────────────────────────────────────────────────────── */}
                {isMultiPhotoSubmission(currentSubmission) && (
                  <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Image:</span>
                        <div className="flex gap-1">
                          {shouldUseStepKeyTabs ? (
                            // StepKey-based tabs (for step-based tasks)
                            getRequiredSteps(currentSubmission).map((stepKey) => {
                              const isActive = activeStepKey === stepKey;
                              const stepLabels = getLabelsForStep(currentSubmission, stepKey);
                              const hasLabels = stepLabels.length > 0 || (stepKey === activeStepKey && boxes.length > 0);
                              const isSaved = stepLabels.length > 0;
                              const displayLabel = getStepDisplayLabel(stepKey, currentTask);
                              
                              return (
                                <button
                                  key={stepKey}
                                  onClick={() => switchStepKey(stepKey)}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {displayLabel}
                                  {isSaved ? (
                                    <span className="flex items-center justify-center w-5 h-5 bg-green-500 text-white rounded-full text-xs">
                                      ✓
                                    </span>
                                  ) : hasLabels ? (
                                    <span className="flex items-center justify-center w-5 h-5 bg-yellow-500 text-white rounded-full text-xs">
                                      •
                                    </span>
                                  ) : (
                                    <span className="flex items-center justify-center w-5 h-5 bg-gray-300 text-gray-600 rounded-full text-xs">
                                      –
                                    </span>
                                  )}
                                </button>
                              );
                            })
                          ) : (
                            // Role-based tabs (legacy)
                            getRequiredRoles(currentSubmission).map((role) => {
                              const isActive = activeRole === role;
                              // Use helper to read labels with fallback priority
                              const mediaItem = currentSubmission.media?.find((m: any) => m.role === role);
                              const stepKey = stepKeyFromMedia(mediaItem);
                              const roleLabels = getLabelsForStep(currentSubmission, stepKey);
                              const hasLabels = roleLabels.length > 0 || (role === activeRole && boxes.length > 0);
                              const isSaved = savedRoles.has(role) || roleLabels.length > 0;
                              
                              return (
                                <button
                                  key={role}
                                  onClick={() => switchRole(role)}
                                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                    isActive
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {getRoleDisplayLabel(role, currentSubmission)}
                                  {isSaved ? (
                                    <span className="flex items-center justify-center w-5 h-5 bg-green-500 text-white rounded-full text-xs">
                                      ✓
                                    </span>
                                  ) : hasLabels ? (
                                    <span className="flex items-center justify-center w-5 h-5 bg-yellow-500 text-white rounded-full text-xs">
                                      •
                                    </span>
                                  ) : (
                                    <span className="flex items-center justify-center w-5 h-5 bg-gray-300 text-gray-600 rounded-full text-xs">
                                      –
                                    </span>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-green-500 rounded-full"></span> Saved
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-yellow-500 rounded-full"></span> Has labels
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 bg-gray-300 rounded-full"></span> Pending
                        </span>
                      </div>
                    </div>
                    {/* Progress indicator - stepKey-based */}
                    {(() => {
                      const requiredStepKeys = getRequiredSteps(currentSubmission);
                      const completedCount = requiredStepKeys.filter(stepKey => {
                        // Use helper to read labels with fallback priority (stepKey-based)
                        const stepLabels = getLabelsForStep(currentSubmission, stepKey);
                        return stepLabels.length > 0;
                      }).length;
                      return (
                        <div className="mt-2 text-xs text-gray-600">
                          Progress: {completedCount} / {requiredStepKeys.length} images labeled
                          {completedCount === requiredStepKeys.length && (
                            <span className="ml-2 text-green-600 font-medium">✓ Ready to complete</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* Canvas Container */}
                <div className="flex-1 flex items-center justify-center">
                <div
                  ref={canvasContainerRef}
                  className="relative w-full h-full flex items-center justify-center"
                  style={{ minHeight: 0 }}
                >
                  <canvas
                    ref={canvasRef}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseLeave={handleCanvasMouseLeave}
                    onMouseMove={handleCanvasMouseMove}
                    className="border border-gray-300 cursor-crosshair block max-w-full max-h-full"
                    style={{ 
                      backgroundColor: '#fff'
                    }}
                  />
                </div>
                </div>
              </div>
              
              {/* Detection Objects, Expected Objects, and Object Library */}
              <div className="w-64 bg-white border border-gray-200 rounded p-3 flex flex-col max-h-full">
                <button
                  onClick={handleSave}
                  disabled={saving || !currentSubmission}
                  className="w-full flex-shrink-0 mb-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Objects'}
                </button>
                {requiredUnansweredCount > 0 && (
                  <p className="text-xs text-red-600 mb-3">
                    {requiredUnansweredCount} required attribute{requiredUnansweredCount === 1 ? '' : 's'} unanswered
                  </p>
                )}
                <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                {/* Click and drag instruction */}
                {selectedCategory && (
                  <div className="text-sm text-gray-500 mb-2">
                    Click and drag to draw
                  </div>
                )}

                {/* Detection Objects */}
                <div className="flex-shrink-0 mb-4 border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Detection Objects</h3>
                    <button
                      onClick={() => setShowDetectionCategories(prev => !prev)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {showDetectionCategories ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showDetectionCategories && (
                    <>
                  {(() => {
                    const featured = (currentSchema?.featuredObjects || []).filter((f: any) => f?.objectId);
                    const hasFeatured = featured.length > 0;
                    const hasClasses = !!(currentSchema?.classes && currentSchema.classes.length > 0);

                    if (hasFeatured) {
                      return (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                          {featured.map((featuredItem: any) => {
                            const objectId = featuredItem.objectId as string;
                            const obj = getCategoryById(objectId);
                            const isUsed = usedCategoryIds.has(objectId);
                            const displayName = obj
                              ? (obj.displayName || obj.name || objectId)
                              : objectId;
                            const description = obj?.description || '-';
                            const objectIdLabel = obj?.name || objectId;
                            return (
                              <button
                                key={objectId}
                                onClick={() => setSelectedCategory(objectId)}
                                className={`w-full text-left px-3 py-2 rounded border ${
                                  selectedCategory === objectId
                                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                                    : isUsed
                                      ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <div className="font-medium text-sm">{displayName}</div>
                                    {isUsed && (
                                      <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-400 bg-gray-200 px-1 rounded">
                                    {featuredItem.hotkey || 'NA'}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {description}
                                </div>
                                <div className="text-xs text-gray-400 mt-1">
                                  Object ID: {objectIdLabel}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      );
                    }

                    if (hasClasses) {
                      return (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                          {currentSchema.classes.map((category: any) => {
                            const isUsed = usedCategoryIds.has(category.id);
                            return (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`w-full text-left px-3 py-2 rounded border ${
                            selectedCategory === category.id
                              ? 'bg-blue-100 border-blue-500 text-blue-700'
                                    : isUsed
                                      ? 'bg-green-50 border-green-300 hover:bg-green-100'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className="font-medium text-sm">{category.displayName || category.label || category.name}</div>
                                    {isUsed && (
                                      <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                            <div className="text-xs text-gray-400 bg-gray-200 px-1 rounded">
                              {category.hotkey || 'NA'}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {category.description || '-'}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Object ID: {category.name || category.id}
                          </div>
                        </button>
                            );
                          })}
                    </div>
                      );
                    }

                    return (
                    <div className="text-center py-4">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500">No detection objects found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Make sure the task has a detection schema assigned
                      </p>
                    </div>
                    );
                  })()}
                    </>
                  )}
                </div>
                
                {/* Object Library */}
                <div className="flex-shrink-0 border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm">Object Library</h4>
                    <button
                      onClick={() => setShowGlobalLibrary(prev => !prev)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {showGlobalLibrary ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  
                  {showGlobalLibrary && (
                    <>
                      {/* Global Library Search Bar - at the top for quick search */}
                      {globalCategoryGroupKeys.length > 0 && (
                        <div className="mb-3">
                      <input
                        type="text"
                            value={globalLibrarySearchTerm}
                            onChange={(e) => setGlobalLibrarySearchTerm(e.target.value)}
                            placeholder="Search all objects..."
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {globalLibrarySearchTerm && (
                            <div className="mt-2 max-h-64 overflow-y-auto space-y-1">
                              {(() => {
                                const searchTerm = globalLibrarySearchTerm.toLowerCase();
                                const allObjects = globalCategoryGroupKeys.flatMap(groupKey => 
                                  (globalCategoryGroups[groupKey] || []).map(obj => ({ ...obj, groupKey }))
                                );
                                const filtered = allObjects.filter(obj =>
                                  objectMatchesGlobalLibrarySearch(obj, obj.groupKey, searchTerm)
                                );
                                
                                if (filtered.length === 0) {
                                  return (
                                    <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-200 rounded">
                                      No objects match this search
                                    </div>
                                  );
                                }
                                
                                return filtered.map((obj: any) => {
                                  const isUsed = usedCategoryIds.has(obj.id);
                                  const groupKey = obj.groupKey;
                                  return (
                          <button
                                      key={`${groupKey}-${obj.id}`}
                            onClick={() => {
                                        setSelectedCategory(obj.id);
                                        const parentKey = groupKeyToParentKey.get(groupKey);
                                        if (parentKey && !expandedParentGroups[parentKey]) {
                                          handleParentToggle(parentKey);
                                        }
                                        if (!expandedGlobalGroups[groupKey]) {
                                          handleGlobalGroupToggle(groupKey);
                                        }
                                        setGlobalLibrarySearchTerm('');
                                      }}
                                      className={`w-full text-left px-3 py-2 rounded border ${
                                        selectedCategory === obj.id
                                          ? 'bg-green-100 border-green-500 text-green-700'
                                          : isUsed
                                            ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                                      <div className="flex items-center space-x-2">
                                        <div className="font-medium text-sm flex-1">{getObjectDisplayName(obj)}</div>
                                        {isUsed && (
                                          <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                            </div>
                                      <div className="text-xs text-gray-500">{formatGroupLabel(groupKey)}</div>
                          </button>
                                  );
                                });
                              })()}
                          </div>
                        )}
                      </div>
                      )}
                      
                      {globalCategoryGroupKeys.length > 0 && !globalLibrarySearchTerm ? (
                        <div className="space-y-3">
                          {globalLibraryParentGroups.map(parent => {
                            const parentExpanded = !!expandedParentGroups[parent.parentKey];
                            return (
                              <div key={parent.parentKey} className="border border-gray-200 rounded">
                                <button
                                  type="button"
                                  onClick={() => handleParentToggle(parent.parentKey)}
                                  className="w-full px-2 py-2 flex items-center space-x-1 text-left hover:bg-gray-50"
                                >
                                  <svg
                                    className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${parentExpanded ? 'transform rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span className="font-medium text-sm flex-1">{parent.displayName}</span>
                                  <span className="text-xs text-gray-500">
                                    {parent.objectCount} {parent.objectCount === 1 ? 'object' : 'objects'}
                                  </span>
                                </button>
                                {parentExpanded && (
                                  <div className="border-t border-gray-100 p-2 space-y-2">
                                    {parent.subcategories.map(sub => {
                                      const groupKey = sub.groupKey;
                                      const groupObjects = sub.objects;
                                      const expanded = !!expandedGlobalGroups[groupKey];
                                      const searchTerm = globalGroupSearchTerms[groupKey] || '';
                                      const filteredObjects = !searchTerm
                                        ? groupObjects
                                        : groupObjects.filter(obj => {
                                            const term = searchTerm.trim().toLowerCase();
                                            const display = getObjectDisplayName(obj).toLowerCase();
                                            const identifier = (obj.name || obj.id || '').toLowerCase();
                                            return display.includes(term) || identifier.includes(term);
                                          });
                                      return (
                                        <div key={groupKey} className="border border-gray-200 rounded ml-4">
                                          <button
                                            type="button"
                                            onClick={() => handleGlobalGroupToggle(groupKey)}
                                            className="w-full px-2 py-2 flex items-center justify-between text-left hover:bg-gray-50"
                                          >
                                            <span className="font-medium text-sm">{sub.displayName}</span>
                                            <span className="text-xs text-gray-500">
                                              {groupObjects.length} {groupObjects.length === 1 ? 'object' : 'objects'}
                                            </span>
                                          </button>
                                          {expanded && (
                                            <div className="border-t border-gray-100 p-2 space-y-2">
                                              <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => handleGlobalGroupSearchChange(groupKey, e.target.value)}
                                                placeholder={`Search ${sub.displayName}...`}
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                              />
                                              <div className="max-h-48 overflow-y-auto space-y-2">
                                                {filteredObjects.length > 0 ? (
                                                  filteredObjects.map(obj => {
                                                    const isUsed = usedCategoryIds.has(obj.id);
                                                    return (
                                                      <button
                                                        key={obj.id}
                                                        onClick={() => setSelectedCategory(obj.id)}
                                                        className={`w-full text-left px-3 py-2 rounded border ${
                                                          selectedCategory === obj.id
                                                            ? 'bg-green-100 border-green-500 text-green-700'
                                                            : isUsed
                                                              ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                                        }`}
                                                      >
                                                        <div className="flex items-center space-x-2">
                                                          <div className="font-medium text-sm flex-1">{getObjectDisplayName(obj)}</div>
                                                          {isUsed && (
                                                            <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                          )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">Object ID: {obj.name || obj.id}</div>
                                                        {obj.description && (
                                                          <div className="text-xs text-gray-400 mt-1 leading-snug overflow-hidden">
                                                            {obj.description}
                                                          </div>
                                                        )}
                                                      </button>
                                                    );
                                                  })
                                                ) : (
                                                  <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-200 rounded">
                                                    No objects match this search
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                    ) : (
                      <div className="text-xs text-gray-500 text-center py-4 border border-dashed border-gray-200 rounded">
                        No global objects available
                      </div>
                    )}
                    </>
                  )}
                </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Image Loaded</h3>
                <p className="text-gray-600">Select a submission from the queue to start labeling</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Queue and Categories */}
        <div className="w-64 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
          {/* Expected Objects */}
          {resolvedExpectedLabels.length > 0 && (
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Expected Labels</h3>
                <button
                  onClick={() => setShowExpectedObjects(prev => !prev)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {showExpectedObjects ? 'Hide' : 'Show'}
                </button>
              </div>
              {showExpectedObjects && (
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {resolvedExpectedLabels.map(item => {
                    const isUsed = usedCategoryIds.has(item.targetId);
                    return (
                      <button
                        key={item.key}
                        disabled={item.missing}
                        onClick={() => !item.missing && setSelectedCategory(item.targetId)}
                        className={`w-full text-left px-3 py-2 rounded border ${
                          item.missing
                            ? 'bg-red-50 border-red-200 text-red-500 cursor-not-allowed'
                            : selectedCategory === item.targetId
                              ? 'bg-blue-100 border-blue-500 text-blue-700'
                              : isUsed
                                ? 'bg-green-50 border-green-300 hover:bg-green-100'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{item.display}</div>
                            {isUsed && (
                              <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span
                            className={`text-[10px] uppercase tracking-wide ${
                              item.source === 'schema'
                                ? 'text-blue-500'
                                : item.source === 'global'
                                  ? 'text-green-500'
                                  : 'text-red-500'
                            }`}
                          >
                            {item.source === 'schema'
                              ? 'Schema'
                              : item.source === 'global'
                                ? 'Global'
                                : 'Missing'}
                          </span>
                        </div>
                        {item.missing && (
                          <div className="text-xs text-red-500 mt-1">
                            Not found in schema/global objects
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Image-level attributes */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="font-semibold mb-3">Image</h3>
            <AttributePanel
              attributes={imageLevelAttributes}
              values={imageAttributeValues}
              onChange={setImageAttributeValues}
              compact
            />
          </div>

          {/* Annotated Objects */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="font-semibold mb-3">Annotated Objects ({boxes.length})</h3>
              {boxes.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {boxes.map((box, index) => {
                    const categoryName = getCategoryName(box.categoryId);
                    const category = currentSchema?.classes?.find((c: any) => c.id === box.categoryId);
                    return (
                      <div
                        key={box.id}
                        className={`p-2 rounded border cursor-pointer ${
                          selectedBox?.id === box.id
                            ? 'bg-red-100 border-red-300'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => setSelectedBox(selectedBox?.id === box.id ? null : box)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setHiddenBoxIds(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(box.id)) {
                                    newSet.delete(box.id);
                                  } else {
                                    newSet.add(box.id);
                                  }
                                  return newSet;
                                });
                              }}
                              className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border transition-colors ${
                                hiddenBoxIds.has(box.id)
                                  ? 'bg-gray-300 border-gray-400 text-gray-600'
                                  : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                              }`}
                              title={hiddenBoxIds.has(box.id) ? 'Show annotation' : 'Hide annotation'}
                            >
                              {hiddenBoxIds.has(box.id) ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {categoryName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {Math.round(box.width)} × {Math.round(box.height)} px
                            </div>
                            {category?.description && (
                              <div className="text-xs text-gray-400 mt-1 truncate">
                                {category.description}
                              </div>
                            )}
                          </div>
                          </div>
                          <div className="flex items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                                // Save to undo history
                                setUndoHistory(prev => [...prev, [...boxesRef.current]]);
                                
                              const newBoxes = boxesRef.current.filter(b => b.id !== box.id);
                              boxesRef.current = newBoxes;
                              setBoxes(newBoxes);
                              // Remove from hidden set if it was hidden
                              setHiddenBoxIds(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(box.id);
                                return newSet;
                              });
                              if (selectedBox?.id === box.id) {
                                setSelectedBox(null);
                              }
                              drawCanvas(newBoxes);
                            }}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            ×
                          </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-500">No objects placed yet</p>
                </div>
              )}
            </div>

          {selectedBox && (
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="font-semibold mb-3">Object Attributes</h3>
              <AttributePanel
                attributes={selectedBoxInstanceAttributes}
                values={selectedBox.attributes || {}}
                onChange={handleBoxAttributeChange}
                flags={selectedBox.sourceLabel?.attributeFlags || {}}
                compact
              />
            </div>
          )}

          {/* Flagged Annotations */}
          {flaggedLabels.length > 0 && (
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="font-semibold mb-3 text-yellow-700">Flagged Annotations ({flaggedLabels.length})</h3>
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {flaggedLabels.map((flaggedLabel, index) => {
                  const categoryName = getCategoryName(flaggedLabel.categoryId);
                  return (
                    <div
                      key={index}
                      className="p-2 rounded border bg-yellow-50 border-yellow-300"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-sm text-yellow-900">
                              {categoryName}
                            </div>
                            <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
                              Flagged
                            </span>
                          </div>
                          {flaggedLabel.flagReason && (
                            <div className="text-xs text-yellow-700 mt-1 font-medium">
                              Reason: {flaggedLabel.flagReason}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            {flaggedLabel.bbox[2] - flaggedLabel.bbox[0]} × {flaggedLabel.bbox[3] - flaggedLabel.bbox[1]} px
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!currentSubmission || !image || !canvasRef.current) return;
                            
                            // Calculate scale
                            const canvas = canvasRef.current;
                            const displayW = canvas.clientWidth;
                            const displayH = canvas.clientHeight;
                            const scaleXToDisplay = image.width > 0 ? displayW / image.width : 1;
                            const scaleYToDisplay = image.height > 0 ? displayH / image.height : 1;
                            const scale = Math.min(scaleXToDisplay, scaleYToDisplay);
                            
                            // Remove this flagged label
                            const updatedFlagged = flaggedLabels.filter((_, i) => i !== index);
                            setFlaggedLabels(updatedFlagged);
                            
                            // Update submission to remove the flag
                            const allLabels = [
                              ...boxes.map((b) => serializeBoxToLabel(b, scale)),
                              ...updatedFlagged
                            ];
                            
                            await updateDoc(doc(db, 'submissions', currentSubmission.id), {
                              labels: allLabels
                            });
                          }}
                          className="text-red-500 hover:text-red-700 text-xs px-2 py-1"
                          title="Remove flag"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

            {/* Queue */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="p-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Queue ({filteredSubmissions.length}{filteredSubmissions.length !== submissions.length ? ` of ${submissions.length}` : ''})</h3>
                <button
                  onClick={() => {
                    if (history.length > 0) {
                      const newHistory = [...history];
                      const prev = newHistory.pop();
                      setHistory(newHistory);
                      if (prev) {
                        loadSubmission(prev, /*pushHistory*/ false);
                      }
                    }
                  }}
                  disabled={history.length === 0}
                  className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Go back to previous submission"
                >
                  ← Previous
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              <div className="space-y-2">
                {filteredSubmissions.map((submission) => (
                  <button
                    key={submission.id}
                    onClick={() => loadSubmission(submission)}
                    className={`w-full text-left p-3 rounded border ${
                      currentSubmission?.id === submission.id
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                      <img
                        src={submission.imageUrl || submission.mediaUrl}
                        alt="Preview"
                        className="w-12 h-12 object-cover rounded"
                      />
                        {/* Multi-photo badge on thumbnail */}
                        {isMultiPhotoSubmission(submission) && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                            {getRequiredSteps(submission).length}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-1">
                          {getTaskName(submission.taskId)}
                          {isMultiPhotoSubmission(submission) && (
                            <span className="text-[10px] text-purple-600">📷</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {submission.category}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(
                            typeof submission.createdAt === 'number' ? submission.createdAt : 
                            (submission.createdAt && typeof submission.createdAt === 'object' && 'toMillis' in submission.createdAt) ? 
                            (submission.createdAt as any).toMillis() : 0
                          ).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Object Modal */}
      {showCreateObjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Create Global Object</h2>
                <button
                  onClick={() => setShowCreateObjectModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <GlobalObjectForm
                categories={objectCategories}
                parentCategories={parentCategories}
                object={undefined}
                onSave={async (formData) => {
                  try {
                    await createCategory(formData);
                    setShowCreateObjectModal(false);
                    // The global categories will automatically refresh via the hook
                  } catch (error) {
                    console.error('Error creating object:', error);
                    alert('Failed to create object. Please try again.');
                  }
                }}
                onCancel={() => setShowCreateObjectModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* QA Review Modal */}
      {showQAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Send to QA Review</h2>
              <button onClick={handleQAModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {currentSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {currentTask?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>Submission ID:</strong> {currentSubmission.id.slice(-8)}
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select QA Review Reason:
              </label>
              <select 
                value={qaReason} 
                onChange={(e) => handleQAReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose a reason...</option>
                {qaReviewReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleQAModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQASubmit}
                disabled={!qaReason.trim() || saving}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {saving ? 'Sending...' : 'Send to QA Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Global Object Form Component (reused from DetectionSetup)
function GlobalObjectForm({ categories, parentCategories, object, onSave, onCancel }: { categories: any[]; parentCategories: any[]; object?: any; onSave: (data: any) => Promise<void>; onCancel: () => void }) {
  const [formData, setFormData] = useState({
    name: object?.name || '',
    displayName: object?.displayName || '',
    description: object?.description || '',
    color: object?.color || '#3b82f6',
    shapes: object?.shapes || ['rectangle'],
    hotkey: object?.hotkey || '',
    category: object?.category || '',
    subcategory: object?.subcategory || object?.category || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const predefinedColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.displayName || !formData.subcategory) {
      alert('Please fill in all required fields (Object ID, Display Name, and Subcategory)');
      return;
    }
    
    if (!formData.shapes || formData.shapes.length === 0) {
      alert('Please select at least one shape');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Object ID *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., cutting_board, knife, plate"
            required
          />
          <p className="text-xs text-gray-500 mt-1">Internal ID (lowercase, underscores)</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name *
          </label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Cutting Board, Knife, Plate"
            required
          />
          <p className="text-xs text-gray-500 mt-1">User-friendly name</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief description of the object"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category (optional)
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">None (Uncategorized)</option>
            {parentCategories.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {parent.displayName}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Top-level category for grouping</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subcategory *
          </label>
          <select
            value={formData.subcategory}
            onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a subcategory...</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.displayName}
              </option>
            ))}
          </select>
          {categories.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              No subcategories available. Create subcategories first.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed Shapes
          </label>
          <div className="grid grid-cols-2 gap-2">
            {['rectangle', 'polygon', 'point', 'line'].map((shape) => (
              <label key={shape} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.shapes.includes(shape)}
                  onChange={(e) => {
                    const currentShapes = formData.shapes;
                    const newShapes = e.target.checked
                      ? [...currentShapes, shape]
                      : currentShapes.filter((s: string) => s !== shape);
                    setFormData({ ...formData, shapes: newShapes });
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 capitalize">{shape}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hotkey (optional)
          </label>
          <input
            type="text"
            value={formData.hotkey}
            onChange={(e) => setFormData({ ...formData, hotkey: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., 1, 2, 3, k"
          />
          <p className="text-xs text-gray-500 mt-1">Keyboard shortcut for quick selection</p>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <div className="flex flex-wrap gap-2">
          {predefinedColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => setFormData({ ...formData, color })}
              className={`w-8 h-8 rounded-full border-2 ${
                formData.color === color ? 'border-gray-400' : 'border-gray-200'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Creating...' : 'Create Object'}
        </button>
      </div>
    </form>
  );
}
